import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    AlertTitle,
    CircularProgress,
    Chip,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tabs,
    Tab,
    Checkbox,
    Stack,
    TablePagination,
    useMediaQuery,
    useTheme,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Article as ArticleIcon,
    Settings as SettingsIcon,
    Close as CloseIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import axiosWithAuth from '../../utils/axiosWithAuth';
import * as casService from '../../services/casService';
import {
    getBusinessProfile,
    updateBusinessProfile,
    createBusinessProfile,
    fetchManagedPropertyById,
    updateManagedProperty,
    deleteManagedProperty,
    getWordPressConfig,
    createWordPressConfig,
    updateWordPressConfig
} from '../../services/casService';

const CASManagement = ({ activeTab: externalActiveTab = null }) => {
    const [loading, setLoading] = useState(true);
    const [casSubscriptions, setCasSubscriptions] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedCAS, setSelectedCAS] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [casForm, setCasForm] = useState({
        subscription_id: '',
        property_id: '',
        api_key_type: 'default', // 'default' or 'custom'
        custom_api_key: '',
        notes: '',
        is_active: true
    });

    const [propertyForm, setPropertyForm] = useState({
        friendly_name: '',
        ga4_property_id: '',
        ga4_property_name: '',
        gsc_property_url: '',
        gsc_property_name: '',
        is_active_for_cycles: true
    });

    const [selectedGA4Property, setSelectedGA4Property] = useState('');
    const [selectedGSCProperty, setSelectedGSCProperty] = useState('');

    const [openPropertyDialog, setOpenPropertyDialog] = useState(false);
    const [availableProperties, setAvailableProperties] = useState({ ga4_properties: [], gsc_properties: [] });
    const [managedProperties, setManagedProperties] = useState([]);

    // Property management modal states
    const [propertyProfileModalOpen, setPropertyProfileModalOpen] = useState(false);
    const [propertySettingsModalOpen, setPropertySettingsModalOpen] = useState(false);
    const [propertyDetailsModalOpen, setPropertyDetailsModalOpen] = useState(false);
    const [selectedPropertyForManagement, setSelectedPropertyForManagement] = useState(null);

    // Property Profile Modal states
    const [selectedPropertyForProfile, setSelectedPropertyForProfile] = useState(null);
    const [propertyProfile, setPropertyProfile] = useState(null);
    const [editedPropertyProfile, setEditedPropertyProfile] = useState(null);
    const [activeProfileSection, setActiveProfileSection] = useState('business_summary');
    const [isProfileLoading, setIsProfileLoading] = useState(false);
    const [isProfileSaving, setIsProfileSaving] = useState(false);
    const [isProfileCreating, setIsProfileCreating] = useState(false);
    const [profileError, setProfileError] = useState('');
    const [showProfileSaveModal, setShowProfileSaveModal] = useState(false);
    const [showCreateProfileModal, setShowCreateProfileModal] = useState(false);

    // Property Settings Modal states
    const [selectedPropertyForSettings, setSelectedPropertyForSettings] = useState(null);
    const [property, setProperty] = useState(null);
    const [wordpressConfig, setWordpressConfig] = useState(null);
    const [isSettingsLoading, setIsSettingsLoading] = useState(false);
    const [isSettingsSaving, setIsSettingsSaving] = useState(false);
    const [settingsError, setSettingsError] = useState('');
    const [activeSettingsTab, setActiveSettingsTab] = useState('property');

    // Delete property state
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
    const [propertySettingsForm, setPropertySettingsForm] = useState({
        friendly_name: '',
        is_active_for_cycles: true,
        ga4_property_id: '',
        ga4_property_name: '',
        gsc_property_url: '',
        gsc_property_name: '',
        site_url: '',
        username: '',
        password: '',
        is_enabled: true,
        auto_publish: true,
        default_status: 'draft',
        default_category: '',
        default_tags: '',
        featured_image_enabled: true,
        seo_meta_enabled: true,
        auto_slug_generation: true,
        rewrite_config: {
            user_config: {
                content_strategy: 'dynamic',
                total_content_per_cycle: 10,
                max_pages_per_cycle: 6,
                max_blogs_per_cycle: 4,
                quality_level: 'medium',
                business_focus: 'balanced',
                content_update_frequency: 'monthly'
            }
        }
    });

    // New nested tabs for batch management with URL state
    const [searchParams, setSearchParams] = useSearchParams();

    const getCasTabFromUrl = () => {
        const subtabParam = searchParams.get('subtab');
        const subtabMap = {
            'analysis': 0,
            'batch': 1,
            'history': 2,
            'assignments': 3
        };
        return subtabMap[subtabParam] !== undefined ? subtabMap[subtabParam] : 0;
    };

    const [activeCasTab, setActiveCasTab] = useState(externalActiveTab !== null ? externalActiveTab : 0);

    // Update activeCasTab when external prop changes
    useEffect(() => {
        if (externalActiveTab !== null) {
            setActiveCasTab(externalActiveTab);
        }
    }, [externalActiveTab]);

    const handleCasTabChange = (e, v) => {
        setActiveCasTab(v);
        // URL will be updated by the useEffect hook
    };

    // Helper functions for date calculations
    const formatLastTrigger = (lastTriggerDate) => {
        if (!lastTriggerDate) return 'Never';

        const date = new Date(lastTriggerDate);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return {
            dateTime: date.toLocaleString(),
            daysAgo: diffDays === 1 ? '1 day ago' : `${diffDays} days ago`
        };
    };

    const isScheduleDue = (lastTriggerDate, schedule) => {
        if (!lastTriggerDate || !schedule) return false;

        const lastDate = new Date(lastTriggerDate);
        const now = new Date();
        const diffTime = Math.abs(now - lastDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        const scheduleDays = {
            'weekly': 7,
            'biweekly': 14,
            'monthly': 30,
            'quarterly': 90,
            'yearly': 365
        };

        return diffDays >= (scheduleDays[schedule] || 30);
    };

    // Batch Items state
    const [batchPropertyId, setBatchPropertyId] = useState('');
    const [items, setItems] = useState([]);
    const [selectedItemIds, setSelectedItemIds] = useState([]);
    const [itemsLoading, setItemsLoading] = useState(false);
    const [itemsError, setItemsError] = useState('');
    const [itemsPage, setItemsPage] = useState(1);
    const [itemsSize, setItemsSize] = useState(20);
    const [itemsTotal, setItemsTotal] = useState(0);
    const [itemsSorting, setItemsSorting] = useState({ sort_by: 'created_at', sort_order: 'desc' });
    const [itemsFilters, setItemsFilters] = useState({
        processing_status: 'pending',
        ready_for_batch: 'true',
        content_type: '',
    });
    const [processingMode, setProcessingMode] = useState('openai_batch');
    const [batchSize, setBatchSize] = useState(20);
    const [startingBatch, setStartingBatch] = useState(false);
    const [batchResult, setBatchResult] = useState(null);

    // Extra filters
    const [decisionStatus, setDecisionStatus] = useState('');
    const [priorityMin, setPriorityMin] = useState('');
    const [priorityMax, setPriorityMax] = useState('');
    const [createdAfter, setCreatedAfter] = useState('');
    const [createdBefore, setCreatedBefore] = useState('');

    // Auto-refresh
    const [autoRefresh, setAutoRefresh] = useState(false);
    useEffect(() => {
        let interval;
        if (autoRefresh && activeCasTab === 1) {
            interval = setInterval(() => loadItems(), 60000);
        }
        return () => { if (interval) clearInterval(interval); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoRefresh, activeCasTab, batchPropertyId, itemsFilters, itemsSorting, itemsPage, itemsSize]);

    const loadItems = async () => {
        setItemsLoading(true);
        setItemsError('');
        try {
            const params = {
                page: itemsPage,
                size: itemsSize,
                sort_by: itemsSorting.sort_by,
                sort_order: itemsSorting.sort_order,
                processing_status: itemsFilters.processing_status,
                ready_for_batch: itemsFilters.ready_for_batch,
                decision_engine_status: decisionStatus || undefined,
                priority_score_min: priorityMin || undefined,
                priority_score_max: priorityMax || undefined,
                created_after: createdAfter || undefined,
                created_before: createdBefore || undefined,
            };
            if (itemsFilters.content_type) params.content_type = itemsFilters.content_type;
            let data;
            if (!batchPropertyId || batchPropertyId === 'all') {
                data = await casService.getAdminAnalysisItemsAll(params);
            } else {
                params.managed_property_id = batchPropertyId;
                data = await casService.getAnalysisItems(batchPropertyId, params);
            }
            setItems(data.items || data.data?.items || []);
            setItemsTotal(data.total || data.data?.total || 0);
        } catch (e) {
            setItemsError(e?.response?.data?.detail || e.message || 'Failed to load items');
        } finally {
            setItemsLoading(false);
        }
    };

    const clearItemFilters = () => {
        setItemsFilters({ processing_status: 'pending', ready_for_batch: 'true', content_type: '' });
        setDecisionStatus('');
        setPriorityMin('');
        setPriorityMax('');
        setCreatedAfter('');
        setCreatedBefore('');
        setItemsSorting({ sort_by: 'created_at', sort_order: 'desc' });
        setItemsPage(1);
    };

    const handleSortClick = (key) => {
        setItemsSorting(prev => ({
            sort_by: key,
            sort_order: prev.sort_by === key && prev.sort_order === 'desc' ? 'asc' : 'desc',
        }));
    };

    useEffect(() => {
        if (activeCasTab === 1) {
            loadItems();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCasTab, batchPropertyId, itemsPage, itemsSize, itemsSorting, itemsFilters]);

    const toggleSelectAll = (checked) => {
        if (checked) {
            setSelectedItemIds(items.map(i => i.id));
        } else {
            setSelectedItemIds([]);
        }
    };

    const toggleSelectOne = (id, checked) => {
        if (checked) setSelectedItemIds(prev => [...new Set([...prev, id])]);
        else setSelectedItemIds(prev => prev.filter(x => x !== id));
    };

    const handleBatchStatusAction = async (action) => {
        if (selectedItemIds.length === 0) {
            setItemsError('Select at least one item');
            return;
        }
        try {
            setItemsLoading(true);
            await casService.batchUpdateStatus(selectedItemIds, action);
            await loadItems();
        } catch (e) {
            setItemsError(e?.response?.data?.detail || e.message || 'Batch update failed');
        } finally {
            setItemsLoading(false);
        }
    };

    const handleStartBatch = async () => {
        if (selectedItemIds.length === 0) {
            setItemsError('Select at least one item');
            return;
        }
        try {
            setStartingBatch(true);
            const res = await casService.startBatchProcessing(selectedItemIds, processingMode, batchSize);
            setBatchResult(res);
            setSelectedItemIds([]);
        } catch (e) {
            setItemsError(e?.response?.data?.detail || e.message || 'Failed to start batch');
        } finally {
            setStartingBatch(false);
        }
    };

    // Batch History state
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const [historySorting, setHistorySorting] = useState({ sort_by: 'created_at', sort_order: 'desc' });
    const [pollingJobs, setPollingJobs] = useState(new Set());

    const loadHistory = async () => {
        setHistoryLoading(true);
        setHistoryError('');
        try {
            const data = await casService.getBatchJobs({
                skip: 0,
                limit: 50,
                sort_by: historySorting.sort_by,
                sort_order: historySorting.sort_order,
            });
            setHistory(data || []);
        } catch (e) {
            setHistoryError(e?.response?.data?.detail || e.message || 'Failed to load batch history');
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        if (activeCasTab === 2) {
            loadHistory();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCasTab, historySorting]);

    // Initialize tab from URL on component mount
    useEffect(() => {
        const tabFromUrl = getCasTabFromUrl();
        setActiveCasTab(tabFromUrl);
    }, []); // Only run on mount

    // Sync URL with CAS sub-tab state
    useEffect(() => {
        const subtabNames = ['analysis', 'batch', 'history', 'assignments'];
        const currentSubtab = subtabNames[activeCasTab];
        if (currentSubtab) {
            setSearchParams(prev => {
                const newParams = new URLSearchParams(prev);
                newParams.set('subtab', currentSubtab);
                return newParams;
            }, { replace: true });
        }
    }, [activeCasTab, setSearchParams]);

    const handleTriggerPoll = async (batchId) => {
        try {
            setPollingJobs(prev => new Set(prev).add(batchId));
            await casService.triggerBatchPoll(batchId);
            await loadHistory();
        } catch (e) {
            setHistoryError(e?.response?.data?.detail || e.message || 'Failed to trigger poll');
        } finally {
            setPollingJobs(prev => { const n = new Set(prev); n.delete(batchId); return n; });
        }
    };

    const handleTriggerAnalysis = async (propertyId) => {
        try {
            await casService.adminTriggerAnalysis(propertyId);
            setSuccess('Analysis triggered successfully');
        } catch (e) {
            setError(e?.response?.data?.detail || e.message || 'Failed to trigger analysis');
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [casRes, subscriptionsRes, availablePropertiesRes, managedPropertiesRes] = await Promise.all([
                axiosWithAuth.get('/admin/cas/subscriptions'),
                axiosWithAuth.get('/admin/subscriptions'),
                axiosWithAuth.get('/cas/properties/available-google-properties'),
                axiosWithAuth.get('/cas/properties/admin/all')
            ]);


            setCasSubscriptions(casRes.data);
            setSubscriptions(subscriptionsRes.data);
            setAvailableProperties(availablePropertiesRes.data || { ga4_properties: [], gsc_properties: [] });
            setManagedProperties(managedPropertiesRes.data || []);
        } catch (error) {
            console.error('Error fetching CAS data:', error);
            setError('Failed to fetch CAS subscriptions');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCAS = async () => {
        if (!casForm.subscription_id || !casForm.property_id) {
            setError('Please fill in all required fields');
            return;
        }

        try {
            setLoading(true);

            // Prepare data for backend
            const submitData = {
                subscription_id: parseInt(casForm.subscription_id),
                property_id: casForm.property_id,
                api_key_type: casForm.api_key_type,
                custom_api_key: casForm.api_key_type === 'custom' ? casForm.custom_api_key : null,
                notes: casForm.notes
            };

            console.log('Submitting CAS subscription data:', submitData);
            await axiosWithAuth.post('/admin/cas/subscriptions', submitData);
            setSuccess('CAS subscription created successfully');
            setOpenDialog(false);
            resetForm();
            fetchData();
        } catch (error) {
            console.error('Error creating CAS subscription:', error);
            setError(error.response?.data?.detail || 'Failed to create CAS subscription');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateCAS = async () => {
        if (!casForm.subscription_id || !casForm.property_id) {
            setError('Please fill in all required fields');
            return;
        }

        try {
            setLoading(true);

            // Prepare data for backend
            const submitData = {
                subscription_id: casForm.subscription_id,
                property_id: casForm.property_id,
                api_key_type: casForm.api_key_type,
                custom_api_key: casForm.api_key_type === 'custom' ? casForm.custom_api_key : undefined,
                notes: casForm.notes,
                is_active: casForm.is_active
            };

            await axiosWithAuth.put(`/admin/cas/subscriptions/${selectedCAS.id}`, submitData);
            setSuccess('CAS subscription updated successfully');
            setOpenDialog(false);
            resetForm();
            fetchData();
        } catch (error) {
            console.error('Error updating CAS subscription:', error);
            setError(error.response?.data?.detail || 'Failed to update CAS subscription');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCAS = async (casId) => {
        if (!window.confirm('Are you sure you want to delete this CAS subscription?')) {
            return;
        }

        try {
            setLoading(true);
            await axiosWithAuth.delete(`/admin/cas/subscriptions/${casId}`);
            setSuccess('CAS subscription deleted successfully');
            fetchData();
        } catch (error) {
            console.error('Error deleting CAS subscription:', error);
            setError('Failed to delete CAS subscription');
        } finally {
            setLoading(false);
        }
    };

    // Mobile-friendly property analysis card component
    const PropertyAnalysisCard = ({ property }) => (
        <Card sx={{ mb: 2, maxWidth: '100%', overflow: 'hidden' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {property.friendly_name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-word' }}>
                            ID: {property.id}
                        </Typography>
                    </Box>
                    <Chip
                        size="small"
                        label={property.is_active_for_cycles ? 'Active' : 'Inactive'}
                        color={property.is_active_for_cycles ? 'success' : 'default'}
                        sx={{ flexShrink: 0 }}
                    />
                </Box>

                <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">GA4 Property</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                            {property.ga4_property_id}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">GSC Property</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {property.gsc_property_url}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Last Trigger</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {(() => {
                                const lastTrigger = formatLastTrigger(property.last_cycle_triggered_at);
                                if (lastTrigger === 'Never') {
                                    return 'Never';
                                }
                                return `${lastTrigger.dateTime} (${lastTrigger.daysAgo})`;
                            })()}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Schedule</Typography>
                        <Typography variant="body2">
                            {(() => {
                                const schedule = property.rewrite_config?.user_config?.content_update_frequency ||
                                    property.rewrite_config?.analysis_schedule ||
                                    property.rewrite_config?.schedule ||
                                    'monthly';
                                return schedule.charAt(0).toUpperCase() + schedule.slice(1);
                            })()}
                        </Typography>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center" sx={{ maxWidth: '100%' }}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleTriggerAnalysis(property.id)}
                        disabled={!property.is_active_for_cycles}
                    >
                        Trigger Analysis
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );

    // Mobile-friendly content batch item card component
    const ContentBatchItemCard = ({ item }) => (
        <Card sx={{ mb: 2, maxWidth: '100%', overflow: 'hidden' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            ID: {item.id}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-word' }}>
                            {item.content_type === 'blog_generation' ? 'Blog' : 'Page'} • {item.managed_property?.friendly_name || `P${item.managed_property_id}`}
                        </Typography>
                    </Box>
                    <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip
                            size="small"
                            label={item.content_type === 'blog_generation' ? 'Blog' : 'Page'}
                            color={item.content_type === 'blog_generation' ? 'secondary' : 'primary'}
                        />
                        <Chip
                            size="small"
                            label={item.processing_status}
                            color={item.processing_status === 'completed' ? 'success' : item.processing_status === 'failed' ? 'error' : 'default'}
                        />
                        <Chip
                            size="small"
                            color={item.ready_for_batch ? 'success' : 'default'}
                            label={item.ready_for_batch ? 'Ready' : 'Not Ready'}
                        />
                    </Box>
                </Box>

                <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Owner</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {item.managed_property?.owner_email ? (item.managed_property.owner_email.split('@')[0]) : '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Decision</Typography>
                        <Typography variant="body2">
                            {item.decision_engine_status || '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">Title/URL</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {item.content_type === 'blog_generation' ? (
                                <>
                                    {item.blog_topic || ''}
                                    {item.blog_category && ` (${item.blog_category})`}
                                </>
                            ) : (
                                item.page_path || item.page_url || ''
                            )}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Created</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {item.created_at ? new Date(item.created_at).toLocaleString() : '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Key Issue</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {item.key_issue_summary || '-'}
                        </Typography>
                    </Grid>
                </Grid>

                {item.batch_info && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">Batch Info</Typography>
                        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                            <Chip size="small" label={(item.batch_info.batch_status || '').replace(/_/g, ' ')} />
                            <Typography variant="body2">
                                {item.batch_info.processed_items}/{item.batch_info.total_items}
                            </Typography>
                            {item.batch_info.openai_batch_id && (
                                <Typography variant="caption" color="textSecondary" sx={{ wordBreak: 'break-all' }}>
                                    {item.batch_info.openai_batch_id.slice(0, 8)}...
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                <Divider sx={{ my: 1 }} />

                <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center" sx={{ maxWidth: '100%' }}>
                    <Checkbox
                        checked={selectedItemIds.includes(item.id)}
                        onChange={(e) => toggleSelectOne(item.id, e.target.checked)}
                        size="small"
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => alert(`View details for ${item.id}`)}
                    >
                        View
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );

    // Mobile-friendly batch history card component
    const BatchHistoryCard = ({ job }) => (
        <Card sx={{ mb: 2, maxWidth: '100%', overflow: 'hidden' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            Batch ID: {(job.batch_id || '').slice(0, 8)}...
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-word' }}>
                            Created: {job.created_at ? new Date(job.created_at).toLocaleString() : '-'}
                        </Typography>
                    </Box>
                    <Chip
                        size="small"
                        label={(job.status || '').replace(/_/g, ' ').toUpperCase()}
                        color={job.status === 'completed' ? 'success' : job.status === 'failed' ? 'error' : 'default'}
                        sx={{ flexShrink: 0 }}
                    />
                </Box>

                <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Progress</Typography>
                        <Typography variant="body2">
                            {job.processed_items}/{job.total_items}
                            {job.failed_items > 0 ? ` | ${job.failed_items} failed` : ''}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Mode</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {(job.processing_mode || '').replace(/_/g, ' ')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">Notes</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {job.notes || '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">Completed</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {job.completed_at ? new Date(job.completed_at).toLocaleString() : '-'}
                        </Typography>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center" sx={{ maxWidth: '100%' }}>
                    {job.openai_batch_id && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleTriggerPoll(job.batch_id)}
                            disabled={historyLoading || pollingJobs.has(job.batch_id)}
                        >
                            {pollingJobs.has(job.batch_id) ? 'Checking...' : 'Re-check'}
                        </Button>
                    )}
                </Box>
            </CardContent>
        </Card>
    );

    // Mobile-friendly subscription card component
    const SubscriptionCard = ({ subscription }) => (
        <Card sx={{ mb: 2, maxWidth: '100%', overflow: 'hidden' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {subscription.company?.name || subscription.owner?.email || 'Unnamed Subscription'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-word' }}>
                            Plan: {subscription.plan?.name || 'N/A'}
                        </Typography>
                    </Box>
                    <Chip
                        label={subscription.status}
                        size="small"
                        color={subscription.status === 'active' ? 'success' : 'default'}
                        sx={{ flexShrink: 0 }}
                    />
                </Box>

                <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">GA4 Property</Typography>
                        <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-word' }}>
                            {subscription.ga4_property_id || 'N/A'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">GSC Property</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {subscription.gsc_property_url || 'N/A'}
                        </Typography>
                    </Grid>
                </Grid>

                <Box display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleEditSubscription(subscription)}
                    >
                        Edit
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );

    // Mobile-friendly CAS subscription card component
    const CASSubscriptionCard = ({ cas }) => (
        <Card sx={{ mb: 2, maxWidth: '100%', overflow: 'hidden' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {getSubscriptionName(cas.subscription_id)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-word' }}>
                            Property: {managedProperties.find(p => (p.ga4_property_id || p.id.toString()) === cas.property_id)?.friendly_name ||
                                cas.property_id}
                        </Typography>
                    </Box>
                    <Chip
                        label={cas.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={cas.is_active ? 'success' : 'default'}
                        sx={{ flexShrink: 0 }}
                    />
                </Box>

                <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Usage Count</Typography>
                        <Typography variant="body2" fontWeight="bold">
                            {cas.usage_count || 0}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Last Used</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {cas.last_used ? new Date(cas.last_used).toLocaleDateString() : 'Never'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">API Key Type</Typography>
                        <Typography variant="body2">
                            {cas.api_key_type || 'Default'}
                        </Typography>
                    </Grid>
                </Grid>

                {cas.notes && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">Notes</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {cas.notes}
                        </Typography>
                    </Box>
                )}

                <Divider sx={{ my: 1 }} />

                <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center" sx={{ maxWidth: '100%' }}>
                    <Tooltip title="Edit CAS Subscription">
                        <IconButton
                            size="medium"
                            onClick={() => openEditCAS(cas)}
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete CAS Subscription">
                        <IconButton
                            size="medium"
                            color="error"
                            onClick={() => handleDeleteCAS(cas.id)}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </CardContent>
        </Card>
    );

    const openEditCAS = (casSubscription) => {
        setSelectedCAS(casSubscription);
        setCasForm({
            subscription_id: casSubscription.subscription_id.toString(),
            property_id: casSubscription.property_id,
            api_key_type: 'default', // Default to default key for editing
            custom_api_key: '',
            notes: casSubscription.notes || '',
            is_active: casSubscription.is_active
        });
        setOpenDialog(true);
    };

    const openCreateCAS = () => {
        setSelectedCAS(null);
        resetForm();
        setOpenDialog(true);
    };

    const handleCreateProperty = async () => {
        try {
            setLoading(true);

            // Build property data from selected GA4 and GSC properties
            let propertyData = {
                ...propertyForm,
                is_active_for_cycles: propertyForm.is_active_for_cycles  // Use form value
            };

            // Handle GA4 property selection
            if (selectedGA4Property) {
                const ga4Property = availableProperties.ga4_properties.find(p => p.id === selectedGA4Property);
                if (ga4Property) {
                    propertyData.ga4_property_id = ga4Property.id;
                    propertyData.ga4_property_name = ga4Property.displayName || ga4Property.name;
                    // Set friendly name from GA4 if not already set
                    if (!propertyData.friendly_name) {
                        propertyData.friendly_name = ga4Property.name;
                    }
                }
            }

            // Handle GSC property selection
            if (selectedGSCProperty) {
                const gscProperty = availableProperties.gsc_properties.find(p => p.id === selectedGSCProperty);
                if (gscProperty) {
                    propertyData.gsc_property_url = gscProperty.id;
                    propertyData.gsc_property_name = gscProperty.name || gscProperty.displayName;
                    // Set friendly name from GSC if not already set
                    if (!propertyData.friendly_name) {
                        propertyData.friendly_name = gscProperty.name;
                    }
                }
            }

            // Ensure we have a friendly name
            if (!propertyData.friendly_name) {
                propertyData.friendly_name = 'New Property';
            }

            await axiosWithAuth.post('/cas/properties', propertyData);
            setSuccess('Property created successfully');
            setOpenPropertyDialog(false);
            resetPropertyForm();
            fetchData();
        } catch (error) {
            console.error('Error creating property:', error);
            setError('Failed to create property');
        } finally {
            setLoading(false);
        }
    };

    const openCreateProperty = () => {
        resetPropertyForm();
        setOpenPropertyDialog(true);
    };

    const resetPropertyForm = () => {
        setPropertyForm({
            friendly_name: '',
            ga4_property_id: '',
            ga4_property_name: '',
            gsc_property_url: '',
            gsc_property_name: '',
            is_active_for_cycles: true
        });
        setSelectedGA4Property('');
        setSelectedGSCProperty('');
    };

    const handleEditSubscription = (subscription) => {
        // TODO: Implement edit subscription functionality
        setError('Edit subscription functionality not yet implemented');
    };

    const handleAssignProperty = (propertyId, propertyType) => {
        // TODO: Implement assign property functionality
        setError('Assign property functionality not yet implemented');
    };

    const resetForm = () => {
        setCasForm({
            subscription_id: '',
            property_id: '',
            api_key_type: 'default',
            custom_api_key: '',
            notes: '',
            is_active: true
        });
    };

    const getSubscriptionName = (subscriptionId) => {
        const subscription = subscriptions.find(sub => sub.id === subscriptionId);
        if (!subscription) return `Subscription #${subscriptionId}`;

        // Prioritize custom subscription name if available
        if (subscription.subscription_name) {
            return subscription.subscription_name;
        }

        const user = subscription.owner;
        const plan = subscription.plan;
        const status = subscription.status;
        const company = subscription.company;

        let displayName = '';

        // Prioritize company name if available
        if (company && company.name) {
            displayName += company.name;
        } else if (user) {
            // Use user's full name if no company
            const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
            if (fullName) {
                displayName += fullName;
            } else {
                displayName += user.email || `User #${subscription.owner_id}`;
            }
        } else {
            displayName += `User #${subscription.owner_id}`;
        }

        // Add plan name
        if (plan && plan.name) {
            displayName += ` - ${plan.name}`;
        }

        // Add status indicator
        if (status) {
            displayName += ` [${status}]`;
        }

        return displayName;
    };

    const getActiveSubscriptions = () => {
        return subscriptions.filter(sub => sub.status === 'active');
    };

    const getAvailablePropertiesForSubscription = (subscriptionId) => {
        if (!subscriptionId) return managedProperties;

        // Get properties already assigned to this subscription
        const assignedProperties = casSubscriptions
            .filter(cas => cas.subscription_id.toString() === subscriptionId && cas.is_active)
            .map(cas => cas.property_id);

        // Return properties that are not already assigned
        return managedProperties.filter(property => {
            const propertyIdToCheck = property.ga4_property_id || property.id.toString();
            return !assignedProperties.includes(propertyIdToCheck);
        });
    };

    if (loading && casSubscriptions.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    // Property Profile functions
    const loadPropertyProfile = async (propertyId = null) => {
        const targetPropertyId = propertyId || selectedPropertyForProfile;
        if (!targetPropertyId) return;

        setIsProfileLoading(true);
        setProfileError('');

        try {
            const profileData = await getBusinessProfile(targetPropertyId);

            if (!profileData) {
                throw new Error('No profile data received from server');
            }

            // Check if profileData is empty or has unexpected structure
            if (Object.keys(profileData).length === 0) {
                throw new Error('Profile data is empty');
            }

            // Check if the response has a nested structure
            let finalProfileData = profileData;
            if (profileData.data) {
                finalProfileData = profileData.data;
            }

            // Create a default profile structure if data is empty or doesn't exist
            const normalizedProfile = {
                business_summary: {
                    business_name: finalProfileData.business_summary?.business_name || '',
                    executive_summary: finalProfileData.business_summary?.executive_summary || '',
                    core_offerings: finalProfileData.business_summary?.core_offerings || '',
                    business_model: finalProfileData.business_summary?.business_model || '',
                    target_audience: finalProfileData.business_summary?.target_audience || '',
                    market_positioning: finalProfileData.business_summary?.market_positioning || ''
                },
                content_strategy: finalProfileData.content_strategy || {
                    pillar_content_themes: [],
                    thought_leadership_themes: [],
                    faq_content_themes: [],
                    case_study_themes: []
                },
                learning_intelligence: finalProfileData.learning_intelligence || {
                    business_keywords: [],
                    industry_terms: [],
                    irrelevant_patterns: [],
                    search_intent_patterns: {},
                    content_themes: [],
                    topic_categories: [],
                    competitive_keywords: [],
                    audience_search_patterns: [],
                    industry_trends: [],
                    solution_keywords: []
                },
                blog_topics: finalProfileData.blog_topics || {
                    primary_topics: [],
                    secondary_topics: [],
                    seasonal_topics: [],
                    trending_topics: [],
                    evergreen_topics: []
                },
                scraped_urls: finalProfileData.scraped_urls || [],
                distilled_context: finalProfileData.distilled_context || ''
            };

            setPropertyProfile(normalizedProfile);
            setEditedPropertyProfile(JSON.parse(JSON.stringify(normalizedProfile))); // Deep copy
        } catch (err) {
            console.error('Error loading profile:', err);

            // If profile doesn't exist, create a default one
            const defaultProfile = {
                business_summary: {
                    business_name: '',
                    executive_summary: '',
                    core_offerings: '',
                    business_model: '',
                    target_audience: '',
                    market_positioning: ''
                },
                content_strategy: {
                    pillar_content_themes: [],
                    thought_leadership_themes: [],
                    faq_content_themes: [],
                    case_study_themes: []
                },
                learning_intelligence: {
                    business_keywords: [],
                    industry_terms: [],
                    irrelevant_patterns: [],
                    search_intent_patterns: {},
                    content_themes: [],
                    topic_categories: [],
                    competitive_keywords: [],
                    audience_search_patterns: [],
                    industry_trends: [],
                    solution_keywords: []
                },
                blog_topics: {
                    primary_topics: [],
                    secondary_topics: [],
                    seasonal_topics: [],
                    trending_topics: [],
                    evergreen_topics: []
                },
                scraped_urls: [],
                distilled_context: ''
            };

            setPropertyProfile(defaultProfile);
            setEditedPropertyProfile(JSON.parse(JSON.stringify(defaultProfile)));
        } finally {
            setIsProfileLoading(false);
        }
    };

    const handleProfileSectionChange = (section) => {
        setActiveProfileSection(section);
    };

    const handleProfileUpdate = (section, subsection, value) => {
        setEditedPropertyProfile(prev => {
            const newProfile = { ...prev };
            if (subsection) {
                if (!newProfile[section]) newProfile[section] = {};
                newProfile[section][subsection] = value;
            } else {
                newProfile[section] = value;
            }
            return newProfile;
        });
    };

    const renderURLManagement = () => (
        <Box sx={{ space: 2 }}>
            <Typography variant="h6" gutterBottom>
                URL Management
            </Typography>

            {/* Summary Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
                        <Typography variant="h4" color="primary.main">
                            {editedPropertyProfile?.scraped_urls?.length || 0}
                        </Typography>
                        <Typography variant="body2" color="primary.main">
                            Total URLs
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                        <Typography variant="h4" color="success.main">
                            {editedPropertyProfile?.scraped_urls?.filter(url => url.status === 'accepted').length || 0}
                        </Typography>
                        <Typography variant="body2" color="success.main">
                            Accepted
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.50' }}>
                        <Typography variant="h4" color="error.main">
                            {editedPropertyProfile?.scraped_urls?.filter(url => url.status === 'rejected').length || 0}
                        </Typography>
                        <Typography variant="body2" color="error.main">
                            Rejected
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
                        <Typography variant="h4" color="warning.main">
                            {editedPropertyProfile?.scraped_urls?.filter(url => url.quality_score >= 0.7).length || 0}
                        </Typography>
                        <Typography variant="body2" color="warning.main">
                            High Quality
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* URL Table */}
            <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                                    URL
                                </th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                                    Classification
                                </th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                                    Purpose
                                </th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                                    Quality Score
                                </th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                                    Status
                                </th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {editedPropertyProfile?.scraped_urls?.map((urlData, index) => (
                                <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '12px' }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'primary.main',
                                                textDecoration: 'none',
                                                '&:hover': { textDecoration: 'underline' },
                                                cursor: 'pointer',
                                                maxWidth: '200px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                            onClick={() => window.open(urlData.url, '_blank')}
                                        >
                                            {urlData.url.length > 50 ? `${urlData.url.substring(0, 50)}...` : urlData.url}
                                        </Typography>
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <Chip
                                            label={urlData.classification || 'other'}
                                            size="small"
                                            color="default"
                                        />
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <Chip
                                            label={urlData.purpose || 'other'}
                                            size="small"
                                            color="secondary"
                                        />
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, height: 8 }}>
                                                <Box
                                                    sx={{
                                                        height: '100%',
                                                        bgcolor: urlData.quality_score >= 0.7 ? 'success.main' : urlData.quality_score >= 0.4 ? 'warning.main' : 'error.main',
                                                        borderRadius: 1,
                                                        width: `${(urlData.quality_score || 0) * 100}%`
                                                    }}
                                                />
                                            </Box>
                                            <Typography variant="caption">
                                                {(urlData.quality_score || 0).toFixed(1)}
                                            </Typography>
                                        </Box>
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <Chip
                                            label={urlData.status || 'accepted'}
                                            size="small"
                                            color={urlData.status === 'accepted' ? 'success' : 'error'}
                                        />
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                                const newUrls = [...editedPropertyProfile.scraped_urls];
                                                newUrls[index].status = newUrls[index].status === 'accepted' ? 'rejected' : 'accepted';
                                                handleProfileUpdate('scraped_urls', null, newUrls);
                                            }}
                                            color={urlData.status === 'accepted' ? 'error' : 'success'}
                                        >
                                            {urlData.status === 'accepted' ? 'Reject' : 'Accept'}
                                        </Button>
                                    </td>
                                </tr>
                            )) || (
                                    <tr>
                                        <td colSpan={6} style={{ padding: '24px', textAlign: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No URLs found. Create a business profile to see classified URLs.
                                            </Typography>
                                        </td>
                                    </tr>
                                )}
                        </tbody>
                    </table>
                </Box>
            </Paper>
        </Box>
    );

    const handleProfileSave = async () => {
        setIsProfileSaving(true);
        try {
            await updateBusinessProfile(selectedPropertyForManagement, editedPropertyProfile);
            setSuccess('Business profile updated successfully!');
            setPropertyProfile(editedPropertyProfile);
            setShowProfileSaveModal(false);
            setPropertyProfileModalOpen(false);
        } catch (err) {
            const errMsg = err.detail || err.message || 'Failed to update business profile.';
            setError(errMsg);
        } finally {
            setIsProfileSaving(false);
        }
    };

    const handleProfileCancel = () => {
        setEditedPropertyProfile(JSON.parse(JSON.stringify(propertyProfile))); // Reset to original
        setShowProfileSaveModal(false);
    };

    const handleOpenCreateProfile = () => {
        setShowCreateProfileModal(true);
    };

    const handleCreateProfile = async () => {
        setIsProfileCreating(true);
        setProfileError('');
        try {
            await createBusinessProfile(selectedPropertyForProfile);
            setSuccess('Business profile creation initiated! This may take a few minutes to complete.');
            // Reload the profile after creation
            await loadPropertyProfile(selectedPropertyForProfile);
            setShowCreateProfileModal(false);
        } catch (err) {
            const errMsg = err.detail || err.message || 'Failed to create business profile.';
            setProfileError(errMsg);
        } finally {
            setIsProfileCreating(false);
        }
    };

    const handleCancelCreateProfile = () => {
        setShowCreateProfileModal(false);
    };

    const handleProfileModalClose = () => {
        setPropertyProfileModalOpen(false);
        // Clear state when modal closes to prevent data bleeding
        setSelectedPropertyForProfile(null);
        setPropertyProfile(null);
        setEditedPropertyProfile(null);
        setProfileError('');
        setActiveProfileSection('business_summary');
    };

    const handleSettingsModalClose = () => {
        setPropertySettingsModalOpen(false);
        // Clear state when modal closes to prevent data bleeding
        setSelectedPropertyForSettings(null);
        setProperty(null);
        setWordpressConfig(null);
        setSettingsError('');
        setActiveSettingsTab('property');
    };

    // Delete property functions
    const handleOpenDeleteProperty = () => {
        setDeleteConfirmationText('');
        setDeleteDialogOpen(true);
    };

    const handleConfirmDeleteProperty = async () => {
        const propertyRecord = managedProperties.find(p => p.id == selectedPropertyForManagement);
        const expectedName = propertyRecord?.friendly_name || '';
        if (deleteConfirmationText !== expectedName) {
            setSettingsError('Property name does not match. Please type the exact property name to confirm deletion.');
            return;
        }
        setIsDeleting(true);
        try {
            await deleteManagedProperty(selectedPropertyForManagement);
            setSuccess('Property deleted successfully!');
            setManagedProperties(prev => prev.filter(p => p.id !== selectedPropertyForManagement));
            setSelectedPropertyForManagement(null);
            setDeleteDialogOpen(false);
            setPropertySettingsModalOpen(false);
        } catch (err) {
            const errMsg = err.detail || err.message || 'Failed to delete property.';
            setSettingsError(errMsg);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCancelDeleteProperty = () => {
        setDeleteDialogOpen(false);
    };

    // Property Settings functions
    const loadPropertySettings = async (propertyId = null) => {
        const targetPropertyId = propertyId || selectedPropertyForSettings;
        if (!targetPropertyId) return;

        setIsSettingsLoading(true);
        setSettingsError('');

        try {
            // Load property data
            const propertyData = await fetchManagedPropertyById(targetPropertyId);

            setProperty(propertyData);
            setPropertySettingsForm({
                friendly_name: propertyData.friendly_name || '',
                is_active_for_cycles: propertyData.is_active_for_cycles !== false,
                ga4_property_id: propertyData.ga4_property_id || '',
                ga4_property_name: propertyData.ga4_property_name || '',
                gsc_property_url: propertyData.gsc_property_url || '',
                gsc_property_name: propertyData.gsc_property_name || '',
                rewrite_config: propertyData.rewrite_config || {}
            });

            // Load WordPress configuration
            try {
                const wpConfig = await getWordPressConfig(targetPropertyId);

                setWordpressConfig(wpConfig);
                setPropertySettingsForm(prev => ({
                    ...prev,
                    site_url: wpConfig.site_url || '',
                    username: wpConfig.username || '',
                    password: '', // Don't show password
                    is_enabled: wpConfig.is_enabled || false,
                    auto_publish: wpConfig.auto_publish || false,
                    default_status: wpConfig.default_status || 'publish',
                    default_category: wpConfig.default_category || '',
                    default_tags: wpConfig.default_tags || [],
                    featured_image_enabled: wpConfig.featured_image_enabled !== false,
                    seo_meta_enabled: wpConfig.seo_meta_enabled !== false,
                    auto_slug_generation: wpConfig.auto_slug_generation !== false
                }));
            } catch (wpError) {

                // Set default WordPress config
                setWordpressConfig(null);
                setPropertySettingsForm(prev => ({
                    ...prev,
                    site_url: '',
                    username: '',
                    password: '',
                    is_enabled: false,
                    auto_publish: false,
                    default_status: 'publish',
                    default_category: '',
                    default_tags: [],
                    featured_image_enabled: true,
                    seo_meta_enabled: true,
                    auto_slug_generation: true
                }));
            }
        } catch (err) {
            const errMsg = err.detail || err.message || 'Failed to load property data.';
            setSettingsError(errMsg);
        } finally {
            setIsSettingsLoading(false);
        }
    };

    const handlePropertySettingsSave = async () => {
        setIsSettingsSaving(true);
        try {
            // Only save editable fields
            const updateData = {
                friendly_name: propertySettingsForm.friendly_name,
                is_active_for_cycles: propertySettingsForm.is_active_for_cycles,
                rewrite_config: propertySettingsForm.rewrite_config
            };

            await updateManagedProperty(selectedPropertyForManagement, updateData);
            setSuccess('Property settings updated successfully!');
            await loadPropertySettings(); // Reload data
        } catch (err) {
            const errMsg = err.detail || err.message || 'Failed to update property settings.';
            setError(errMsg);
        } finally {
            setIsSettingsSaving(false);
        }
    };

    const handleWordPressConfigSave = async () => {
        setIsSettingsSaving(true);
        try {
            if (wordpressConfig) {
                // Update existing configuration
                await updateWordPressConfig(wordpressConfig.id, {
                    site_url: propertySettingsForm.site_url,
                    username: propertySettingsForm.username,
                    password: propertySettingsForm.password || undefined, // Only send if provided
                    is_enabled: propertySettingsForm.is_enabled,
                    auto_publish: propertySettingsForm.auto_publish,
                    default_status: propertySettingsForm.default_status,
                    default_category: propertySettingsForm.default_category,
                    default_tags: propertySettingsForm.default_tags,
                    featured_image_enabled: propertySettingsForm.featured_image_enabled,
                    seo_meta_enabled: propertySettingsForm.seo_meta_enabled,
                    auto_slug_generation: propertySettingsForm.auto_slug_generation
                });
            } else {
                // Create new configuration
                await createWordPressConfig({
                    managed_property_id: selectedPropertyForManagement,
                    site_url: propertySettingsForm.site_url,
                    username: propertySettingsForm.username,
                    password: propertySettingsForm.password,
                    is_enabled: propertySettingsForm.is_enabled,
                    auto_publish: propertySettingsForm.auto_publish,
                    default_status: propertySettingsForm.default_status,
                    default_category: propertySettingsForm.default_category,
                    default_tags: propertySettingsForm.default_tags,
                    featured_image_enabled: propertySettingsForm.featured_image_enabled,
                    seo_meta_enabled: propertySettingsForm.seo_meta_enabled,
                    auto_slug_generation: propertySettingsForm.auto_slug_generation
                });
            }
            setSuccess('WordPress configuration updated successfully!');
            await loadPropertySettings(); // Reload data
        } catch (err) {
            const errMsg = err.detail || err.message || 'Failed to update WordPress configuration.';
            setError(errMsg);
        } finally {
            setIsSettingsSaving(false);
        }
    };

    return (
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
            {/* Alerts */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} sx={{ width: '100%', overflow: 'hidden' }}>
                <Typography variant="h6" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                    CAS (Content Analysis Service) Management
                </Typography>
            </Box>

            {/* Add CAS sub-tabs - only show when not controlled externally */}
            {externalActiveTab === null && (
                <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3, width: '100%', overflow: 'hidden' }}>
                    <Tabs
                        value={activeCasTab}
                        onChange={handleCasTabChange}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                        allowScrollButtonsMobile
                        sx={{
                            '& .MuiTab-root': {
                                minWidth: { xs: 'auto', sm: 'auto' },
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                padding: { xs: '6px 8px', sm: '12px 16px' }
                            }
                        }}
                    >
                        <Tab label={isMobile ? "Trigger" : "Trigger Analysis"} />
                        <Tab label={isMobile ? "Batch Items" : "Batch Items (Admin)"} />
                        <Tab label={isMobile ? "History" : "Batch History (Admin)"} />
                        <Tab label="Assignments" />
                    </Tabs>
                </Paper>
            )}

            {activeCasTab === 0 && (externalActiveTab === null || externalActiveTab === 0) && (
                <Box>
                    <Paper sx={{ p: { xs: 1, sm: 3 }, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Trigger Analysis
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Trigger analysis for any property. This will start the analysis cycle for the selected property.
                        </Typography>

                        {loading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                <CircularProgress />
                            </Box>
                        ) : Array.isArray(managedProperties) && managedProperties.length > 0 ? (
                            isMobile ? (
                                // Mobile view - Cards
                                <Box>
                                    {managedProperties.map(property => (
                                        <PropertyAnalysisCard key={property.id} property={property} />
                                    ))}
                                </Box>
                            ) : (
                                // Desktop view - Table
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Property ID</TableCell>
                                                <TableCell>Friendly Name</TableCell>
                                                <TableCell>GA4 Property</TableCell>
                                                <TableCell>GSC Property</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Last Trigger</TableCell>
                                                <TableCell>Schedule</TableCell>
                                                <TableCell>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {managedProperties.map(property => (
                                                <TableRow key={property.id}>
                                                    <TableCell>{property.id}</TableCell>
                                                    <TableCell>{property.friendly_name}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" fontFamily="monospace">
                                                            {property.ga4_property_id}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" noWrap title={property.gsc_property_url}>
                                                            {property.gsc_property_url}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={property.is_active_for_cycles ? 'Active' : 'Inactive'}
                                                            color={property.is_active_for_cycles ? 'success' : 'default'}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {(() => {
                                                            const lastTrigger = formatLastTrigger(property.last_cycle_triggered_at);
                                                            if (lastTrigger === 'Never') {
                                                                return (
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        Never
                                                                    </Typography>
                                                                );
                                                            }
                                                            return (
                                                                <Box>
                                                                    <Typography variant="caption" display="block">
                                                                        {lastTrigger.dateTime}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        {lastTrigger.daysAgo}
                                                                    </Typography>
                                                                </Box>
                                                            );
                                                        })()}
                                                    </TableCell>
                                                    <TableCell>
                                                        {(() => {
                                                            // Extract schedule from rewrite_config or default to monthly
                                                            const schedule = property.rewrite_config?.user_config?.content_update_frequency ||
                                                                property.rewrite_config?.analysis_schedule ||
                                                                property.rewrite_config?.schedule ||
                                                                'monthly';
                                                            const isDue = isScheduleDue(property.last_cycle_triggered_at, schedule);
                                                            return (
                                                                <Chip
                                                                    size="small"
                                                                    label={schedule.charAt(0).toUpperCase() + schedule.slice(1)}
                                                                    color={isDue ? 'error' : 'default'}
                                                                    variant={isDue ? 'filled' : 'outlined'}
                                                                />
                                                            );
                                                        })()}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => handleTriggerAnalysis(property.id)}
                                                            disabled={!property.is_active_for_cycles}
                                                        >
                                                            Trigger Analysis
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )
                        ) : (
                            <Box textAlign="center" py={4}>
                                <Typography variant="body2" color="textSecondary">
                                    No properties found
                                </Typography>
                            </Box>
                        )}
                    </Paper>


                </Box>
            )
            }

            {
                activeCasTab === 1 && (externalActiveTab === null || externalActiveTab === 1) && (
                    <Paper sx={{ p: { xs: 1, sm: 3 }, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Content Batch Management (Admin)
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Select ready items, update statuses, and trigger batch processing.
                        </Typography>

                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            justifyContent: 'space-between',
                            gap: { xs: 2, sm: 0 },
                            mb: 2
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                flexWrap: 'wrap'
                            }}>
                                <Checkbox checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
                                <Typography variant="caption">Auto-refresh (60s)</Typography>
                                <Button size="small" variant="outlined" onClick={() => loadItems()}>Manual Refresh</Button>
                            </Box>
                            <Typography variant="caption" color="textSecondary">Showing {items.length} of {itemsTotal}</Typography>
                        </Box>

                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Property</InputLabel>
                                    <Select
                                        label="Property"
                                        value={batchPropertyId}
                                        onChange={(e) => { setBatchPropertyId(e.target.value); setItemsPage(1); }}
                                    >
                                        <MenuItem value="all"><em>All</em></MenuItem>
                                        <MenuItem value=""><em>Select a property...</em></MenuItem>
                                        {managedProperties.map((p) => (
                                            <MenuItem key={p.id} value={p.id}>
                                                {p.friendly_name} ({p.ga4_property_id})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Processing Status</InputLabel>
                                    <Select
                                        label="Processing Status"
                                        value={itemsFilters.processing_status}
                                        onChange={(e) => setItemsFilters(prev => ({ ...prev, processing_status: e.target.value }))}
                                    >
                                        <MenuItem value="">All</MenuItem>
                                        <MenuItem value="pending">Pending</MenuItem>
                                        <MenuItem value="ready_for_batch">Ready For Batch</MenuItem>
                                        <MenuItem value="in_progress">In Progress</MenuItem>
                                        <MenuItem value="completed">Completed</MenuItem>
                                        <MenuItem value="failed">Failed</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl fullWidth>
                                    <InputLabel>Ready</InputLabel>
                                    <Select
                                        label="Ready"
                                        value={itemsFilters.ready_for_batch}
                                        onChange={(e) => setItemsFilters(prev => ({ ...prev, ready_for_batch: e.target.value }))}
                                    >
                                        <MenuItem value="true">True</MenuItem>
                                        <MenuItem value="false">False</MenuItem>
                                        <MenuItem value="">Any</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Content Type</InputLabel>
                                    <Select
                                        label="Content Type"
                                        value={itemsFilters.content_type}
                                        onChange={(e) => setItemsFilters(prev => ({ ...prev, content_type: e.target.value }))}
                                    >
                                        <MenuItem value=""><em>Any</em></MenuItem>
                                        <MenuItem value="blog_generation">Blog</MenuItem>
                                        <MenuItem value="page_rewrite">Page</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Decision</InputLabel>
                                    <Select label="Decision" value={decisionStatus} onChange={e => setDecisionStatus(e.target.value)}>
                                        <MenuItem value="">All</MenuItem>
                                        <MenuItem value="pending_decision">Pending</MenuItem>
                                        <MenuItem value="selected_for_rewrite">Selected</MenuItem>
                                        <MenuItem value="ignored_recently_rewritten">Recently Rewritten</MenuItem>
                                        <MenuItem value="deferred_cycle_capacity_met">Deferred</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={6} md={3}>
                                <TextField label="Min Priority" type="number" inputProps={{ step: 0.01, min: 0, max: 1 }} fullWidth value={priorityMin} onChange={e => setPriorityMin(e.target.value)} />
                            </Grid>
                            <Grid item xs={6} sm={6} md={3}>
                                <TextField label="Max Priority" type="number" inputProps={{ step: 0.01, min: 0, max: 1 }} fullWidth value={priorityMax} onChange={e => setPriorityMax(e.target.value)} />
                            </Grid>
                            <Grid item xs={6} sm={6} md={3}>
                                <TextField label="Created After" type="date" InputLabelProps={{ shrink: true }} fullWidth value={createdAfter} onChange={e => setCreatedAfter(e.target.value)} />
                            </Grid>
                            <Grid item xs={6} sm={6} md={3}>
                                <TextField label="Created Before" type="date" InputLabelProps={{ shrink: true }} fullWidth value={createdBefore} onChange={e => setCreatedBefore(e.target.value)} />
                            </Grid>
                        </Grid>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: { xs: 1, sm: 0 },
                            mb: 2
                        }}>
                            <Button variant="outlined" onClick={clearItemFilters}>Clear Filters</Button>
                            <Typography variant="caption" color="textSecondary">Sorted by {itemsSorting.sort_by} ({itemsSorting.sort_order})</Typography>
                        </Box>

                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Processing Mode</InputLabel>
                                    <Select
                                        label="Processing Mode"
                                        value={processingMode}
                                        onChange={(e) => setProcessingMode(e.target.value)}
                                    >
                                        <MenuItem value="openai_batch">OpenAI Batch</MenuItem>
                                        <MenuItem value="sequential">Sequential</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    type="number"
                                    label="Batch Size"
                                    fullWidth
                                    value={batchSize}
                                    onChange={(e) => setBatchSize(parseInt(e.target.value || '0', 10))}
                                    inputProps={{ min: 1, max: 100 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: 1,
                                    justifyContent: { xs: 'stretch', sm: 'flex-end' },
                                    flexWrap: 'wrap'
                                }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleBatchStatusAction('mark_ready')}
                                        disabled={itemsLoading || !batchPropertyId}
                                        size="small"
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        Enable Batch
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleBatchStatusAction('mark_not_ready')}
                                        disabled={itemsLoading || !batchPropertyId}
                                        size="small"
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        Disable Batch
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleBatchStatusAction('reset_status')}
                                        disabled={itemsLoading || !batchPropertyId}
                                        size="small"
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        Reset Status
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => handleBatchStatusAction('mark_rejected')}
                                        disabled={itemsLoading || selectedItemIds.length === 0}
                                        size="small"
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        Reject Selected
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleStartBatch}
                                        disabled={startingBatch || itemsLoading || selectedItemIds.length === 0}
                                        size="small"
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        Start Batch
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>

                        {itemsError && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setItemsError('')}>{itemsError}</Alert>
                        )}

                        {itemsLoading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                <CircularProgress />
                            </Box>
                        ) : items.length === 0 ? (
                            <Box textAlign="center" py={4}>
                                <Typography variant="body2" color="textSecondary">
                                    No items found
                                </Typography>
                            </Box>
                        ) : isMobile ? (
                            // Mobile view - Cards
                            <Box>
                                {/* Select All Checkbox for Mobile */}
                                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                    <Checkbox
                                        indeterminate={selectedItemIds.length > 0 && selectedItemIds.length < items.length}
                                        checked={items.length > 0 && selectedItemIds.length === items.length}
                                        onChange={(e) => toggleSelectAll(e.target.checked)}
                                    />
                                    <Typography variant="body2">
                                        Select All ({selectedItemIds.length}/{items.length})
                                    </Typography>
                                </Box>
                                {items.map(item => (
                                    <ContentBatchItemCard key={item.id} item={item} />
                                ))}
                            </Box>
                        ) : (
                            // Desktop view - Table
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    indeterminate={selectedItemIds.length > 0 && selectedItemIds.length < items.length}
                                                    checked={items.length > 0 && selectedItemIds.length === items.length}
                                                    onChange={(e) => toggleSelectAll(e.target.checked)}
                                                />
                                            </TableCell>
                                            <TableCell onClick={() => handleSortClick('id')} sx={{ cursor: 'pointer' }}>ID</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell onClick={() => handleSortClick('managed_property_id')} sx={{ cursor: 'pointer' }}>Property</TableCell>
                                            <TableCell>Owner</TableCell>
                                            <TableCell>Title/URL</TableCell>
                                            <TableCell onClick={() => handleSortClick('processing_status')} sx={{ cursor: 'pointer' }}>Status</TableCell>
                                            <TableCell onClick={() => handleSortClick('decision_engine_status')} sx={{ cursor: 'pointer' }}>Decision</TableCell>
                                            <TableCell onClick={() => handleSortClick('ready_for_batch')} sx={{ cursor: 'pointer' }}>Ready</TableCell>
                                            <TableCell onClick={() => handleSortClick('created_at')} sx={{ cursor: 'pointer' }}>Created</TableCell>
                                            <TableCell>Key Issue</TableCell>
                                            <TableCell>Batch Info</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.map(item => (
                                            <TableRow key={item.id} hover>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedItemIds.includes(item.id)}
                                                        onChange={(e) => toggleSelectOne(item.id, e.target.checked)}
                                                    />
                                                </TableCell>
                                                <TableCell>{item.id}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={item.content_type === 'blog_generation' ? 'Blog' : 'Page'} color={item.content_type === 'blog_generation' ? 'secondary' : 'primary'} />
                                                </TableCell>
                                                <TableCell title={item.managed_property?.friendly_name || `P${item.managed_property_id}`}>
                                                    {item.managed_property?.friendly_name || `P${item.managed_property_id}`}
                                                </TableCell>
                                                <TableCell>
                                                    {item.managed_property?.owner_email ? (item.managed_property.owner_email.split('@')[0]) : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {item.content_type === 'blog_generation' ? (
                                                        <Box>
                                                            <Typography variant="body2" noWrap>{item.blog_topic || ''}</Typography>
                                                            {item.blog_category && <Typography variant="caption" color="textSecondary">({item.blog_category})</Typography>}
                                                        </Box>
                                                    ) : (
                                                        <Box>
                                                            <Typography variant="body2" color="primary" noWrap sx={{ cursor: 'pointer' }} onClick={() => item.page_url && window.open(item.page_url, '_blank')}>{item.page_path || item.page_url || ''}</Typography>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={item.processing_status} />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={item.decision_engine_status || '-'} />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip size="small" color={item.ready_for_batch ? 'success' : 'default'} label={item.ready_for_batch ? 'Yes' : 'No'} />
                                                </TableCell>
                                                <TableCell>{item.created_at ? new Date(item.created_at).toLocaleString() : '-'}</TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" noWrap>{item.key_issue_summary || '-'}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {item.batch_info ? (
                                                        <Box>
                                                            <Chip size="small" label={(item.batch_info.batch_status || '').replace(/_/g, ' ')} />
                                                            <Typography variant="caption">{item.batch_info.processed_items}/{item.batch_info.total_items}</Typography>
                                                            {item.batch_info.openai_batch_id && (
                                                                <Typography variant="caption" color="textSecondary" noWrap>{item.batch_info.openai_batch_id.slice(0, 8)}...</Typography>
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="caption" color="textSecondary">-</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Button size="small" variant="outlined" onClick={() => alert(`View details for ${item.id}`)}>View</Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {batchResult && (
                            <Box sx={{ mt: 2 }}>
                                <Alert severity="info">Batch started. ID: {batchResult.batch_id || batchResult?.data?.batch_id || 'N/A'}</Alert>
                            </Box>
                        )}

                        <Box display="flex" justifyContent="flex-end" mt={2}>
                            <TablePagination
                                component="div"
                                count={itemsTotal}
                                page={itemsPage - 1}
                                onPageChange={(e, p) => setItemsPage(p + 1)}
                                rowsPerPage={itemsSize}
                                onRowsPerPageChange={(e) => { setItemsSize(parseInt(e.target.value, 10)); setItemsPage(1); }}
                                rowsPerPageOptions={[10, 20, 50, 100]}
                            />
                        </Box>
                    </Paper>
                )
            }

            {
                activeCasTab === 2 && (externalActiveTab === null || externalActiveTab === 2) && (
                    <Paper sx={{ p: { xs: 1, sm: 3 }, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Batch Cycles History (Admin)
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            View and manage batch processing history and trigger status poll.
                        </Typography>

                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Sort By</InputLabel>
                                    <Select
                                        label="Sort By"
                                        value={historySorting.sort_by}
                                        onChange={(e) => setHistorySorting(prev => ({ ...prev, sort_by: e.target.value }))}
                                    >
                                        <MenuItem value="created_at">Created</MenuItem>
                                        <MenuItem value="status">Status</MenuItem>
                                        <MenuItem value="total_items">Total Items</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Order</InputLabel>
                                    <Select
                                        label="Order"
                                        value={historySorting.sort_order}
                                        onChange={(e) => setHistorySorting(prev => ({ ...prev, sort_order: e.target.value }))}
                                    >
                                        <MenuItem value="desc">Desc</MenuItem>
                                        <MenuItem value="asc">Asc</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {historyError && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHistoryError('')}>{historyError}</Alert>
                        )}

                        {historyLoading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                <CircularProgress />
                            </Box>
                        ) : Array.isArray(history) && history.length > 0 ? (
                            isMobile ? (
                                // Mobile view - Cards
                                <Box>
                                    {history.map(job => (
                                        <BatchHistoryCard key={job.id || job.batch_id} job={job} />
                                    ))}
                                </Box>
                            ) : (
                                // Desktop view - Table
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Batch ID</TableCell>
                                                <TableCell>Created</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Progress</TableCell>
                                                <TableCell>Mode</TableCell>
                                                <TableCell>Notes</TableCell>
                                                <TableCell>Completed</TableCell>
                                                <TableCell>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {history.map(job => (
                                                <TableRow key={job.id || job.batch_id}>
                                                    <TableCell title={job.batch_id}><Typography variant="caption" fontFamily="monospace">{(job.batch_id || '').slice(0, 8)}...</Typography></TableCell>
                                                    <TableCell>{job.created_at ? new Date(job.created_at).toLocaleString() : '-'}</TableCell>
                                                    <TableCell><Chip size="small" label={(job.status || '').replace(/_/g, ' ').toUpperCase()} /></TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption">{job.processed_items}/{job.total_items}{job.failed_items > 0 ? ` | ${job.failed_items} failed` : ''}</Typography>
                                                    </TableCell>
                                                    <TableCell><Typography variant="caption">{(job.processing_mode || '').replace(/_/g, ' ')}</Typography></TableCell>
                                                    <TableCell><Typography variant="caption" noWrap title={job.notes || ''}>{job.notes || '-'}</Typography></TableCell>
                                                    <TableCell>{job.completed_at ? new Date(job.completed_at).toLocaleString() : '-'}</TableCell>
                                                    <TableCell>
                                                        {job.openai_batch_id && (
                                                            <Button size="small" variant="outlined" onClick={() => handleTriggerPoll(job.batch_id)} disabled={historyLoading || pollingJobs.has(job.batch_id)}>
                                                                {pollingJobs.has(job.batch_id) ? 'Checking...' : 'Re-check'}
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )
                        ) : (
                            <Box textAlign="center" py={4}>
                                <Typography variant="body2" color="textSecondary">
                                    No history found
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                )
            }

            {
                activeCasTab === 3 && (externalActiveTab === null || externalActiveTab === 3) && (
                    <Box>
                        {/* Statistics */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>
                                            Total Subscriptions
                                        </Typography>
                                        <Typography variant="h4">
                                            {subscriptions.length}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>
                                            Active Subscriptions
                                        </Typography>
                                        <Typography variant="h4">
                                            {getActiveSubscriptions().length}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>
                                            CAS Assignments
                                        </Typography>
                                        <Typography variant="h4">
                                            {casSubscriptions.length}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>
                                            Available Properties
                                        </Typography>
                                        <Typography variant="h4">
                                            {availableProperties.ga4_properties.length + availableProperties.gsc_properties.length}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Property Management Buttons */}
                        <Box
                            display="flex"
                            justifyContent={isMobile ? "center" : "flex-end"}
                            flexWrap="wrap"
                            gap={1}
                            sx={{ mb: 3 }}
                        >
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={openCreateProperty}
                                disabled={loading}
                                fullWidth={isMobile}
                                sx={{ minWidth: isMobile ? 'auto' : '140px' }}
                            >
                                {isMobile ? "Create Property" : "Create Property"}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={openCreateCAS}
                                disabled={loading}
                                fullWidth={isMobile}
                                sx={{ minWidth: isMobile ? 'auto' : '200px' }}
                            >
                                {isMobile ? "Assign Property" : "Assign Property to Subscription"}
                            </Button>
                        </Box>

                        {/* CAS Subscriptions Display */}
                        <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3, width: '100%', overflow: 'hidden' }}>
                            <Typography variant="h6" gutterBottom>
                                CAS Subscriptions
                            </Typography>
                            {loading ? (
                                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                    <CircularProgress />
                                </Box>
                            ) : isMobile ? (
                                // Mobile view - Cards
                                <Box>
                                    {casSubscriptions.filter(cas => cas.is_active).map((cas) => (
                                        <CASSubscriptionCard key={cas.id} cas={cas} />
                                    ))}
                                </Box>
                            ) : (
                                // Desktop view - Table
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Subscription</TableCell>
                                                <TableCell>Property</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Usage Count</TableCell>
                                                <TableCell>Last Used</TableCell>
                                                <TableCell>Notes</TableCell>
                                                <TableCell>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {casSubscriptions.filter(cas => cas.is_active).map((cas) => (
                                                <TableRow key={cas.id}>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {getSubscriptionName(cas.subscription_id)}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                ID: {cas.subscription_id}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={cas.property_id}
                                                            color="primary"
                                                            size="small"
                                                        />
                                                        <Typography variant="caption" display="block" color="textSecondary">
                                                            {managedProperties.find(p => (p.ga4_property_id || p.id.toString()) === cas.property_id)?.friendly_name || 'Unknown Property'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={cas.is_active ? 'Active' : 'Inactive'}
                                                            color={cas.is_active ? 'success' : 'error'}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {cas.usage_count || 0}
                                                    </TableCell>
                                                    <TableCell>
                                                        {cas.last_used ? new Date(cas.last_used).toLocaleDateString() : 'Never'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {cas.notes || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Tooltip title="Edit CAS Subscription">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => openEditCAS(cas)}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete CAS Subscription">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleDeleteCAS(cas.id)}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>

                        {/* Available Properties */}
                        <Paper sx={{ p: { xs: 1, sm: 2 }, width: '100%', overflow: 'hidden' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} lg={6}>
                                    <Paper sx={{ p: { xs: 1, sm: 2 }, width: '100%', overflow: 'hidden' }}>
                                        <Typography variant="h6" gutterBottom>
                                            Available GA4 Properties
                                        </Typography>
                                        {loading ? (
                                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                                                <CircularProgress />
                                            </Box>
                                        ) : isMobile ? (
                                            // Mobile view - Cards
                                            <Box>
                                                {availableProperties.ga4_properties.map((property) => (
                                                    <Card key={property.id} sx={{ mb: 2, maxWidth: '100%', overflow: 'hidden' }}>
                                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                                                    <Typography variant="subtitle2" sx={{ minWidth: 0, flex: 1, wordBreak: 'break-word', overflow: 'hidden' }}>
                                                                        {property.displayName || property.name || 'Unnamed Property'}
                                                                    </Typography>
                                                                    <Chip
                                                                        label={property.id}
                                                                        size="small"
                                                                        sx={{ flexShrink: 0, fontFamily: 'monospace' }}
                                                                    />
                                                                </Box>
                                                                {property.websiteUrl && (
                                                                    <Typography variant="caption" color="textSecondary" sx={{ wordBreak: 'break-all' }}>
                                                                        Website: {property.websiteUrl}
                                                                    </Typography>
                                                                )}
                                                                {property.timeZone && (
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        Timezone: {property.timeZone}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </Box>
                                        ) : (
                                            // Desktop view - Table
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Property ID</TableCell>
                                                            <TableCell>Display Name</TableCell>
                                                            <TableCell>Website URL</TableCell>
                                                            <TableCell>Timezone</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {availableProperties.ga4_properties.map((property) => (
                                                            <TableRow key={property.id}>
                                                                <TableCell>
                                                                    <Typography variant="caption" fontFamily="monospace">
                                                                        {property.id}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>{property.displayName || property.name || 'Unnamed Property'}</TableCell>
                                                                <TableCell>
                                                                    <Typography variant="caption" noWrap title={property.websiteUrl}>
                                                                        {property.websiteUrl || 'N/A'}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="caption">
                                                                        {property.timeZone || 'N/A'}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <Paper sx={{ p: { xs: 1, sm: 2 }, width: '100%', overflow: 'hidden' }}>
                                        <Typography variant="h6" gutterBottom>
                                            Available GSC Properties
                                        </Typography>
                                        {loading ? (
                                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                                                <CircularProgress />
                                            </Box>
                                        ) : isMobile ? (
                                            // Mobile view - Cards
                                            <Box>
                                                {availableProperties.gsc_properties.map((property, index) => (
                                                    <Card key={index} sx={{ mb: 2, maxWidth: '100%', overflow: 'hidden' }}>
                                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                                                    <Typography variant="subtitle2" sx={{ minWidth: 0, flex: 1, wordBreak: 'break-word', overflow: 'hidden' }}>
                                                                        {property.name || property.displayName || 'GSC Property'}
                                                                    </Typography>
                                                                    <Chip
                                                                        label="GSC"
                                                                        size="small"
                                                                        color="secondary"
                                                                        sx={{ flexShrink: 0 }}
                                                                    />
                                                                </Box>
                                                                <Typography variant="body2" sx={{ wordBreak: 'break-all', overflow: 'hidden' }}>
                                                                    {property.siteUrl}
                                                                </Typography>
                                                                {property.permissionLevel && (
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        Permission: {property.permissionLevel}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </Box>
                                        ) : (
                                            // Desktop view - Table
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Property Name</TableCell>
                                                            <TableCell>Site URL</TableCell>
                                                            <TableCell>Permission Level</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {availableProperties.gsc_properties.map((property, index) => (
                                                            <TableRow key={index}>
                                                                <TableCell>
                                                                    <Typography variant="caption">
                                                                        {property.name || property.displayName || 'GSC Property'}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="caption" noWrap title={property.siteUrl}>
                                                                        {property.siteUrl}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="caption">
                                                                        {property.permissionLevel || 'N/A'}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Managed Properties Section */}
                        <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3, width: '100%', overflow: 'hidden' }}>
                            <Typography variant="h6" gutterBottom>
                                All Managed Properties
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Manage all properties created in the CAS system. Click the manage buttons to view/edit property details, settings, and profile.
                            </Typography>

                            {loading ? (
                                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                    <CircularProgress />
                                </Box>
                            ) : isMobile ? (
                                // Mobile view - Cards
                                <Box>
                                    {managedProperties.map((property) => (
                                        <Card key={property.id} sx={{ mb: 2 }}>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    {property.friendly_name}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                                    ID: {property.id}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                                    GA4: {property.ga4_property_id || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                                    GSC: {property.gsc_property_url || 'N/A'}
                                                </Typography>
                                                <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
                                                    <Chip
                                                        size="small"
                                                        label={property.is_active_for_cycles ? 'Active' : 'Inactive'}
                                                        color={property.is_active_for_cycles ? 'success' : 'default'}
                                                    />
                                                </Box>
                                                <Divider sx={{ my: 2 }} />
                                                <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center" sx={{ width: '100%', overflow: 'hidden' }}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<EditIcon />}
                                                        onClick={async () => {
                                                            // Set the specific property for profile modal
                                                            setSelectedPropertyForProfile(property.id);
                                                            // Clear any previous data immediately
                                                            setPropertyProfile(null);
                                                            setEditedPropertyProfile(null);
                                                            setProfileError('');
                                                            setActiveProfileSection('business_summary');
                                                            // Open modal
                                                            setPropertyProfileModalOpen(true);
                                                            // Load data for the specific property
                                                            await loadPropertyProfile(property.id);
                                                        }}
                                                        sx={{ minWidth: 'auto', flex: '1 1 auto' }}
                                                    >
                                                        Profile
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<SettingsIcon />}
                                                        onClick={async () => {
                                                            // Set the specific property for settings modal
                                                            setSelectedPropertyForSettings(property.id);
                                                            // Clear any previous data immediately
                                                            setProperty(null);
                                                            setWordpressConfig(null);
                                                            setSettingsError('');
                                                            setActiveSettingsTab('property');
                                                            // Reset form to default state
                                                            setPropertySettingsForm({
                                                                friendly_name: '',
                                                                is_active_for_cycles: true,
                                                                ga4_property_id: '',
                                                                ga4_property_name: '',
                                                                gsc_property_url: '',
                                                                gsc_property_name: '',
                                                                site_url: '',
                                                                username: '',
                                                                password: '',
                                                                is_enabled: true,
                                                                auto_publish: true,
                                                                default_status: 'draft',
                                                                default_category: '',
                                                                default_tags: '',
                                                                featured_image_enabled: true,
                                                                seo_meta_enabled: true,
                                                                auto_slug_generation: true,
                                                                rewrite_config: {
                                                                    user_config: {
                                                                        content_strategy: 'dynamic',
                                                                        total_content_per_cycle: 10,
                                                                        max_pages_per_cycle: 6,
                                                                        max_blogs_per_cycle: 4,
                                                                        quality_level: 'medium',
                                                                        business_focus: 'balanced',
                                                                        content_update_frequency: 'monthly'
                                                                    }
                                                                }
                                                            });
                                                            // Open modal
                                                            setPropertySettingsModalOpen(true);
                                                            // Load data for the specific property
                                                            await loadPropertySettings(property.id);
                                                        }}
                                                        sx={{ minWidth: 'auto', flex: '1 1 auto' }}
                                                    >
                                                        Settings
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<ViewIcon />}
                                                        onClick={() => {
                                                            setSelectedPropertyForManagement(property.id);
                                                            setPropertyDetailsModalOpen(true);
                                                        }}
                                                        sx={{ minWidth: 'auto', flex: '1 1 auto' }}
                                                    >
                                                        Details
                                                    </Button>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Box>
                            ) : (
                                // Desktop view - Table
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>ID</TableCell>
                                                <TableCell>Friendly Name</TableCell>
                                                <TableCell>GA4 Property ID</TableCell>
                                                <TableCell>GSC Property URL</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {managedProperties.map((property) => (
                                                <TableRow key={property.id}>
                                                    <TableCell>{property.id}</TableCell>
                                                    <TableCell>{property.friendly_name}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" fontFamily="monospace">
                                                            {property.ga4_property_id || 'N/A'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" noWrap title={property.gsc_property_url}>
                                                            {property.gsc_property_url || 'N/A'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={property.is_active_for_cycles ? 'Active' : 'Inactive'}
                                                            color={property.is_active_for_cycles ? 'success' : 'default'}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box display="flex" gap={1}>
                                                            <Tooltip title="View/Edit Profile">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={async () => {
                                                                        // Set the specific property for profile modal
                                                                        setSelectedPropertyForProfile(property.id);
                                                                        // Clear any previous data immediately
                                                                        setPropertyProfile(null);
                                                                        setEditedPropertyProfile(null);
                                                                        setProfileError('');
                                                                        setActiveProfileSection('business_summary');
                                                                        // Open modal
                                                                        setPropertyProfileModalOpen(true);
                                                                        // Load data for the specific property
                                                                        await loadPropertyProfile(property.id);
                                                                    }}
                                                                >
                                                                    <EditIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Property Settings">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={async () => {
                                                                        // Set the specific property for settings modal
                                                                        setSelectedPropertyForSettings(property.id);
                                                                        // Clear any previous data immediately
                                                                        setProperty(null);
                                                                        setWordpressConfig(null);
                                                                        setSettingsError('');
                                                                        setActiveSettingsTab('property');
                                                                        // Reset form to default state
                                                                        setPropertySettingsForm({
                                                                            friendly_name: '',
                                                                            is_active_for_cycles: true,
                                                                            ga4_property_id: '',
                                                                            ga4_property_name: '',
                                                                            gsc_property_url: '',
                                                                            gsc_property_name: '',
                                                                            site_url: '',
                                                                            username: '',
                                                                            password: '',
                                                                            is_enabled: true,
                                                                            auto_publish: true,
                                                                            default_status: 'draft',
                                                                            default_category: '',
                                                                            default_tags: '',
                                                                            featured_image_enabled: true,
                                                                            seo_meta_enabled: true,
                                                                            auto_slug_generation: true,
                                                                            rewrite_config: {
                                                                                user_config: {
                                                                                    content_strategy: 'dynamic',
                                                                                    total_content_per_cycle: 10,
                                                                                    max_pages_per_cycle: 6,
                                                                                    max_blogs_per_cycle: 4,
                                                                                    quality_level: 'medium',
                                                                                    business_focus: 'balanced',
                                                                                    content_update_frequency: 'monthly'
                                                                                }
                                                                            }
                                                                        });
                                                                        // Open modal
                                                                        setPropertySettingsModalOpen(true);
                                                                        // Load data for the specific property
                                                                        await loadPropertySettings(property.id);
                                                                    }}
                                                                >
                                                                    <SettingsIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Property Details">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setSelectedPropertyForManagement(property.id);
                                                                        setPropertyDetailsModalOpen(true);
                                                                    }}
                                                                >
                                                                    <ViewIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>
                    </Box>
                )
            }

            {/* CAS Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
                <DialogTitle>
                    {selectedCAS ? 'Edit Property Assignment' : 'Assign Property to Subscription'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Active Subscription</InputLabel>
                                <Select
                                    value={casForm.subscription_id}
                                    onChange={(e) => setCasForm({ ...casForm, subscription_id: e.target.value })}
                                    label="Active Subscription"
                                >
                                    <MenuItem value="">
                                        <em>Choose an active subscription...</em>
                                    </MenuItem>
                                    {getActiveSubscriptions().map((subscription) => (
                                        <MenuItem key={subscription.id} value={subscription.id.toString()}>
                                            {getSubscriptionName(subscription.id)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Property</InputLabel>
                                <Select
                                    value={casForm.property_id}
                                    onChange={(e) => setCasForm({ ...casForm, property_id: e.target.value })}
                                    label="Property"
                                >
                                    <MenuItem value="">
                                        <em>Choose a property...</em>
                                    </MenuItem>
                                    {getAvailablePropertiesForSubscription(casForm.subscription_id).map((property) => (
                                        <MenuItem
                                            key={property.id}
                                            value={property.ga4_property_id || property.id.toString()}
                                        >
                                            {property.friendly_name} (ID: {property.id})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {casForm.subscription_id && (
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                    Note: You can assign multiple properties to the same subscription.
                                    Only available (unassigned) properties are shown for the selected subscription.
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>API Key Type</InputLabel>
                                <Select
                                    value={casForm.api_key_type}
                                    onChange={(e) => setCasForm({ ...casForm, api_key_type: e.target.value })}
                                    label="API Key Type"
                                >
                                    <MenuItem value="default">Use Default Key</MenuItem>
                                    <MenuItem value="custom">Use Custom Key</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {casForm.api_key_type === 'custom' && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Custom API Key"
                                    type="password"
                                    value={casForm.custom_api_key}
                                    onChange={(e) => setCasForm({ ...casForm, custom_api_key: e.target.value })}
                                    helperText="Enter your custom CAS API key"
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Notes"
                                multiline
                                rows={3}
                                value={casForm.notes}
                                onChange={(e) => setCasForm({ ...casForm, notes: e.target.value })}
                                helperText="Optional notes about this CAS subscription"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={casForm.is_active}
                                    onChange={(e) => setCasForm({ ...casForm, is_active: e.target.value })}
                                    label="Status"
                                >
                                    <MenuItem value={true}>Active</MenuItem>
                                    <MenuItem value={false}>Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">
                                Note: Choose between the default CAS API key or provide a custom key for this subscription.
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={selectedCAS ? handleUpdateCAS : handleCreateCAS}
                        variant="contained"
                        disabled={loading}
                    >
                        {selectedCAS ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Property Creation Dialog */}
            <Dialog open={openPropertyDialog} onClose={() => setOpenPropertyDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
                <DialogTitle>
                    Create New Property
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Friendly Name"
                                value={propertyForm.friendly_name}
                                onChange={(e) => setPropertyForm({ ...propertyForm, friendly_name: e.target.value })}
                                helperText="Enter a friendly name for the property"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom>
                                Select Google Analytics 4 Property
                            </Typography>
                            <FormControl fullWidth>
                                <InputLabel>GA4 Property</InputLabel>
                                <Select
                                    value={selectedGA4Property}
                                    onChange={(e) => {
                                        setSelectedGA4Property(e.target.value);
                                        if (e.target.value) {
                                            const ga4Property = availableProperties.ga4_properties.find(p => p.id === e.target.value);
                                            if (ga4Property) {
                                                setPropertyForm(prev => ({
                                                    ...prev,
                                                    ga4_property_name: ga4Property.displayName || ga4Property.name,
                                                    friendly_name: prev.friendly_name || ga4Property.name
                                                }));
                                            }
                                        } else {
                                            setPropertyForm(prev => ({
                                                ...prev,
                                                ga4_property_name: ''
                                            }));
                                        }
                                    }}
                                    label="GA4 Property"
                                >
                                    <MenuItem value="">
                                        <em>Choose a GA4 property...</em>
                                    </MenuItem>
                                    {availableProperties.ga4_properties.map((property) => (
                                        <MenuItem key={property.id} value={property.id}>
                                            {property.name} ({property.id})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="GA4 Property Name"
                                value={propertyForm.ga4_property_name}
                                onChange={(e) => setPropertyForm({ ...propertyForm, ga4_property_name: e.target.value })}
                                helperText="GA4 property name (auto-filled when GA4 property is selected)"
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom>
                                Select Google Search Console Property
                            </Typography>
                            <FormControl fullWidth>
                                <InputLabel>GSC Property</InputLabel>
                                <Select
                                    value={selectedGSCProperty}
                                    onChange={(e) => {
                                        setSelectedGSCProperty(e.target.value);
                                        if (e.target.value) {
                                            const gscProperty = availableProperties.gsc_properties.find(p => p.id === e.target.value);
                                            if (gscProperty) {
                                                setPropertyForm(prev => ({
                                                    ...prev,
                                                    gsc_property_name: gscProperty.name || gscProperty.displayName,
                                                    friendly_name: prev.friendly_name || gscProperty.name
                                                }));
                                            }
                                        } else {
                                            setPropertyForm(prev => ({
                                                ...prev,
                                                gsc_property_name: ''
                                            }));
                                        }
                                    }}
                                    label="GSC Property"
                                >
                                    <MenuItem value="">
                                        <em>Choose a GSC property...</em>
                                    </MenuItem>
                                    {availableProperties.gsc_properties.map((property) => (
                                        <MenuItem key={property.id} value={property.id}>
                                            {property.name} ({property.id})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="GSC Property Name"
                                value={propertyForm.gsc_property_name}
                                onChange={(e) => setPropertyForm({ ...propertyForm, gsc_property_name: e.target.value })}
                                helperText="GSC property name (auto-filled when GSC property is selected)"
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Active for Cycles</InputLabel>
                                <Select
                                    value={propertyForm.is_active_for_cycles}
                                    onChange={(e) => setPropertyForm({ ...propertyForm, is_active_for_cycles: e.target.value })}
                                    label="Active for Cycles"
                                >
                                    <MenuItem value={true}>Yes (Active)</MenuItem>
                                    <MenuItem value={false}>No (Inactive)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>
                                Note: You can select both GA4 and GSC properties, or just one of them.
                                The friendly name will be auto-filled from the selected properties.
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPropertyDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateProperty}
                        variant="contained"
                        disabled={loading || (!selectedGA4Property && !selectedGSCProperty)}
                    >
                        Create Property
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Property Profile Modal */}
            <Dialog open={propertyProfileModalOpen} onClose={handleProfileModalClose} maxWidth="lg" fullWidth>
                <DialogTitle>Edit Profile - {managedProperties.find(p => p.id === selectedPropertyForProfile)?.friendly_name}</DialogTitle>
                <DialogContent>
                    {isProfileLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <CircularProgress />
                        </Box>
                    ) : profileError ? (
                        <Box textAlign="center" py={4}>
                            <Typography color="error">{profileError}</Typography>
                        </Box>
                    ) : !propertyProfile ? (
                        <Box textAlign="center" py={4}>
                            <Typography variant="h6">No business profile found</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Please create a business profile first.
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            {/* Navigation Tabs */}
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                                <Tabs
                                    value={activeProfileSection}
                                    onChange={(e, newValue) => handleProfileSectionChange(newValue)}
                                    variant={isMobile ? "scrollable" : "standard"}
                                    scrollButtons={isMobile ? "auto" : false}
                                    allowScrollButtonsMobile
                                    sx={{
                                        '& .MuiTab-root': {
                                            minWidth: { xs: 'auto', sm: 'auto' },
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                            padding: { xs: '6px 8px', sm: '12px 16px' }
                                        }
                                    }}
                                >
                                    <Tab label={isMobile ? "Summary" : "Business Summary"} value="business_summary" />
                                    <Tab label={isMobile ? "Intelligence" : "Learning Intelligence"} value="learning_intelligence" />
                                    <Tab label={isMobile ? "Strategy" : "Content Strategy"} value="content_strategy" />
                                    <Tab label={isMobile ? "Blogs" : "Blog Topics"} value="blog_topics" />
                                    <Tab label={isMobile ? "URLs" : "URL Management"} value="url_management" />
                                </Tabs>
                            </Box>

                            {/* Content Area */}
                            <Box>
                                {activeProfileSection === 'business_summary' && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Business Name"
                                                value={editedPropertyProfile?.business_summary?.business_name || ''}
                                                onChange={(e) => handleProfileUpdate('business_summary', 'business_name', e.target.value)}
                                                placeholder="Enter the business name..."
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                label="Executive Summary"
                                                value={editedPropertyProfile?.business_summary?.executive_summary || ''}
                                                onChange={(e) => handleProfileUpdate('business_summary', 'executive_summary', e.target.value)}
                                                placeholder="Brief overview of the business..."
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Core Offerings"
                                                value={editedPropertyProfile?.business_summary?.core_offerings || ''}
                                                onChange={(e) => handleProfileUpdate('business_summary', 'core_offerings', e.target.value)}
                                                placeholder="Main products/services..."
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Business Model"
                                                value={editedPropertyProfile?.business_summary?.business_model || ''}
                                                onChange={(e) => handleProfileUpdate('business_summary', 'business_model', e.target.value)}
                                                placeholder="How the business operates..."
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Target Audience"
                                                value={editedPropertyProfile?.business_summary?.target_audience || ''}
                                                onChange={(e) => handleProfileUpdate('business_summary', 'target_audience', e.target.value)}
                                                placeholder="Who they serve..."
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Market Positioning"
                                                value={editedPropertyProfile?.business_summary?.market_positioning || ''}
                                                onChange={(e) => handleProfileUpdate('business_summary', 'market_positioning', e.target.value)}
                                                placeholder="Competitive positioning..."
                                            />
                                        </Grid>
                                    </Grid>
                                )}

                                {activeProfileSection === 'learning_intelligence' && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Business Keywords"
                                                value={editedPropertyProfile?.learning_intelligence?.business_keywords?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('learning_intelligence', 'business_keywords', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter keywords separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Industry Terms"
                                                value={editedPropertyProfile?.learning_intelligence?.industry_terms?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('learning_intelligence', 'industry_terms', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter industry terms separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Content Themes"
                                                value={editedPropertyProfile?.learning_intelligence?.content_themes?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('learning_intelligence', 'content_themes', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter content themes separated by commas"
                                            />
                                        </Grid>
                                    </Grid>
                                )}

                                {activeProfileSection === 'content_strategy' && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Pillar Content Themes"
                                                value={editedPropertyProfile?.content_strategy?.pillar_content_themes?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('content_strategy', 'pillar_content_themes', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter pillar content themes separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Thought Leadership Themes"
                                                value={editedPropertyProfile?.content_strategy?.thought_leadership_themes?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('content_strategy', 'thought_leadership_themes', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter thought leadership themes separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="FAQ Content Themes"
                                                value={editedPropertyProfile?.content_strategy?.faq_content_themes?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('content_strategy', 'faq_content_themes', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter FAQ content themes separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Case Study Themes"
                                                value={editedPropertyProfile?.content_strategy?.case_study_themes?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('content_strategy', 'case_study_themes', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter case study themes separated by commas"
                                            />
                                        </Grid>
                                    </Grid>
                                )}

                                {activeProfileSection === 'blog_topics' && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Primary Topics"
                                                value={editedPropertyProfile?.blog_topics?.primary_topics?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('blog_topics', 'primary_topics', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter primary blog topics separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Secondary Topics"
                                                value={editedPropertyProfile?.blog_topics?.secondary_topics?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('blog_topics', 'secondary_topics', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter secondary blog topics separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Seasonal Topics"
                                                value={editedPropertyProfile?.blog_topics?.seasonal_topics?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('blog_topics', 'seasonal_topics', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter seasonal blog topics separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Trending Topics"
                                                value={editedPropertyProfile?.blog_topics?.trending_topics?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('blog_topics', 'trending_topics', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter trending blog topics separated by commas"
                                            />
                                        </Grid>
                                    </Grid>
                                )}

                                {activeProfileSection === 'url_management' && (
                                    <Box>
                                        {renderURLManagement()}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleProfileModalClose}>Cancel</Button>
                    <Button
                        variant="outlined"
                        onClick={handleOpenCreateProfile}
                        disabled={isProfileCreating}
                        startIcon={<RefreshIcon />}
                    >
                        {isProfileCreating ? 'Creating...' : (propertyProfile ? 'Recreate Profile' : 'Create Profile')}
                    </Button>
                    {propertyProfile && (
                        <Button
                            variant="contained"
                            onClick={() => setShowProfileSaveModal(true)}
                            disabled={isProfileSaving}
                        >
                            {isProfileSaving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Profile Save Confirmation Modal */}
            <Dialog open={showProfileSaveModal} onClose={() => setShowProfileSaveModal(false)}>
                <DialogTitle>Save Profile Changes</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to save these changes to the business profile?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleProfileCancel}>Cancel</Button>
                    <Button variant="contained" onClick={handleProfileSave} disabled={isProfileSaving}>
                        {isProfileSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create/Recreate Profile Confirmation Modal */}
            <Dialog open={showCreateProfileModal} onClose={handleCancelCreateProfile} maxWidth="sm" fullWidth>
                <DialogTitle>{propertyProfile ? 'Recreate Business Profile' : 'Create Business Profile'}</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <AlertTitle>Profile Generation</AlertTitle>
                        {propertyProfile
                            ? 'This will regenerate the business profile using AI analysis. The existing profile will be replaced with a new one.'
                            : 'This will create a new business profile using AI analysis of your website content. This process may take a few minutes to complete.'
                        }
                    </Alert>
                    <Typography>
                        {propertyProfile
                            ? 'Are you sure you want to recreate the business profile? This action cannot be undone.'
                            : 'Are you sure you want to create a new business profile? This will analyze your website and generate a comprehensive profile.'
                        }
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreateProfile}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateProfile}
                        disabled={isProfileCreating}
                        color="primary"
                    >
                        {isProfileCreating ? 'Creating...' : (propertyProfile ? 'Recreate Profile' : 'Create Profile')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Property Settings Modal */}
            <Dialog open={propertySettingsModalOpen} onClose={handleSettingsModalClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            Property Settings: {managedProperties.find(p => p.id === selectedPropertyForSettings)?.friendly_name}
                        </Typography>
                        <IconButton onClick={handleSettingsModalClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {isSettingsLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <CircularProgress />
                        </Box>
                    ) : settingsError ? (
                        <Box textAlign="center" py={4}>
                            <Typography color="error">{settingsError}</Typography>
                        </Box>
                    ) : (
                        <Box>
                            {/* Tab Navigation */}
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                                <Tabs
                                    value={activeSettingsTab}
                                    onChange={(e, newValue) => setActiveSettingsTab(newValue)}
                                    variant={isMobile ? "scrollable" : "standard"}
                                    scrollButtons={isMobile ? "auto" : false}
                                    allowScrollButtonsMobile
                                    sx={{
                                        '& .MuiTab-root': {
                                            minWidth: { xs: 'auto', sm: 'auto' },
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                            padding: { xs: '6px 8px', sm: '12px 16px' }
                                        }
                                    }}
                                >
                                    <Tab label={isMobile ? "Property" : "Property Settings"} value="property" />
                                    <Tab label={isMobile ? "WordPress" : "WordPress Configuration"} value="wordpress" />
                                </Tabs>
                            </Box>

                            {/* Property Settings Tab */}
                            {activeSettingsTab === 'property' && (
                                <Box sx={{ space: 3 }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Friendly Name"
                                                value={propertySettingsForm.friendly_name || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, friendly_name: e.target.value })}
                                                placeholder="Enter a friendly name for this property"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="GA4 Property ID"
                                                value={propertySettingsForm.ga4_property_id || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, ga4_property_id: e.target.value })}
                                                placeholder="GA4 Property ID"
                                                InputProps={{ readOnly: true }}
                                                helperText="Google Analytics 4 Property ID (read-only)"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="GA4 Property Name"
                                                value={propertySettingsForm.ga4_property_name || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, ga4_property_name: e.target.value })}
                                                placeholder="GA4 Property Name"
                                                InputProps={{ readOnly: true }}
                                                helperText="Google Analytics 4 Property Name (read-only)"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="GSC Property URL"
                                                value={propertySettingsForm.gsc_property_url || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, gsc_property_url: e.target.value })}
                                                placeholder="https://example.com"
                                                InputProps={{ readOnly: true }}
                                                helperText="Google Search Console Property URL (read-only)"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="GSC Property Name"
                                                value={propertySettingsForm.gsc_property_name || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, gsc_property_name: e.target.value })}
                                                placeholder="GSC Property Name"
                                                InputProps={{ readOnly: true }}
                                                helperText="Google Search Console Property Name (read-only)"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox
                                                        checked={propertySettingsForm.is_active_for_cycles || false}
                                                        onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, is_active_for_cycles: e.target.checked })}
                                                    />
                                                    <Typography>Active for Content Cycles</Typography>
                                                </Box>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Note: GA4 and GSC properties are read-only as they are managed by the system.
                                                Only the friendly name and active status can be modified.
                                            </Typography>
                                        </Grid>

                                        {/* Rewrite Configuration Section */}
                                        <Grid item xs={12}>
                                            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                                                Content Rewrite Configuration
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Content Strategy</InputLabel>
                                                <Select
                                                    value={propertySettingsForm.rewrite_config?.user_config?.content_strategy || 'dynamic'}
                                                    onChange={(e) => setPropertySettingsForm({
                                                        ...propertySettingsForm,
                                                        rewrite_config: {
                                                            ...propertySettingsForm.rewrite_config,
                                                            user_config: {
                                                                ...propertySettingsForm.rewrite_config?.user_config,
                                                                content_strategy: e.target.value
                                                            }
                                                        }
                                                    })}
                                                    label="Content Strategy"
                                                >
                                                    <MenuItem value="dynamic">Dynamic</MenuItem>
                                                    <MenuItem value="balanced">Balanced</MenuItem>
                                                    <MenuItem value="pages_only">Pages Only</MenuItem>
                                                    <MenuItem value="blogs_only">Blogs Only</MenuItem>
                                                    <MenuItem value="mixed">Mixed</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Total Content Per Cycle"
                                                type="number"
                                                value={propertySettingsForm.rewrite_config?.user_config?.total_content_per_cycle || 10}
                                                onChange={(e) => setPropertySettingsForm({
                                                    ...propertySettingsForm,
                                                    rewrite_config: {
                                                        ...propertySettingsForm.rewrite_config,
                                                        user_config: {
                                                            ...propertySettingsForm.rewrite_config?.user_config,
                                                            total_content_per_cycle: parseInt(e.target.value)
                                                        }
                                                    }
                                                })}
                                                inputProps={{ min: 1, max: 50 }}
                                                helperText="Number of content pieces to generate per cycle"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Max Pages Per Cycle"
                                                type="number"
                                                value={propertySettingsForm.rewrite_config?.user_config?.max_pages_per_cycle || 6}
                                                onChange={(e) => setPropertySettingsForm({
                                                    ...propertySettingsForm,
                                                    rewrite_config: {
                                                        ...propertySettingsForm.rewrite_config,
                                                        user_config: {
                                                            ...propertySettingsForm.rewrite_config?.user_config,
                                                            max_pages_per_cycle: parseInt(e.target.value)
                                                        }
                                                    }
                                                })}
                                                inputProps={{ min: 0, max: 20 }}
                                                helperText="Maximum pages to rewrite per cycle"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Max Blogs Per Cycle"
                                                type="number"
                                                value={propertySettingsForm.rewrite_config?.user_config?.max_blogs_per_cycle || 4}
                                                onChange={(e) => setPropertySettingsForm({
                                                    ...propertySettingsForm,
                                                    rewrite_config: {
                                                        ...propertySettingsForm.rewrite_config,
                                                        user_config: {
                                                            ...propertySettingsForm.rewrite_config?.user_config,
                                                            max_blogs_per_cycle: parseInt(e.target.value)
                                                        }
                                                    }
                                                })}
                                                inputProps={{ min: 0, max: 20 }}
                                                helperText="Maximum blog posts to generate per cycle"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Quality Level</InputLabel>
                                                <Select
                                                    value={propertySettingsForm.rewrite_config?.user_config?.quality_level || 'medium'}
                                                    onChange={(e) => setPropertySettingsForm({
                                                        ...propertySettingsForm,
                                                        rewrite_config: {
                                                            ...propertySettingsForm.rewrite_config,
                                                            user_config: {
                                                                ...propertySettingsForm.rewrite_config?.user_config,
                                                                quality_level: e.target.value
                                                            }
                                                        }
                                                    })}
                                                    label="Quality Level"
                                                >
                                                    <MenuItem value="low">Low</MenuItem>
                                                    <MenuItem value="medium">Medium</MenuItem>
                                                    <MenuItem value="high">High</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Business Focus</InputLabel>
                                                <Select
                                                    value={propertySettingsForm.rewrite_config?.user_config?.business_focus || 'balanced'}
                                                    onChange={(e) => setPropertySettingsForm({
                                                        ...propertySettingsForm,
                                                        rewrite_config: {
                                                            ...propertySettingsForm.rewrite_config,
                                                            user_config: {
                                                                ...propertySettingsForm.rewrite_config?.user_config,
                                                                business_focus: e.target.value
                                                            }
                                                        }
                                                    })}
                                                    label="Business Focus"
                                                >
                                                    <MenuItem value="balanced">Balanced</MenuItem>
                                                    <MenuItem value="seo_uplift">SEO Uplift</MenuItem>
                                                    <MenuItem value="engagement_improvement">Engagement Improvement</MenuItem>
                                                    <MenuItem value="conversion_optimization">Conversion Optimization</MenuItem>
                                                    <MenuItem value="traffic_growth">Traffic Growth</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Content Update Frequency</InputLabel>
                                                <Select
                                                    value={propertySettingsForm.rewrite_config?.user_config?.content_update_frequency || 'monthly'}
                                                    onChange={(e) => setPropertySettingsForm({
                                                        ...propertySettingsForm,
                                                        rewrite_config: {
                                                            ...propertySettingsForm.rewrite_config,
                                                            user_config: {
                                                                ...propertySettingsForm.rewrite_config?.user_config,
                                                                content_update_frequency: e.target.value
                                                            }
                                                        }
                                                    })}
                                                    label="Content Update Frequency"
                                                >
                                                    <MenuItem value="weekly">Weekly</MenuItem>
                                                    <MenuItem value="biweekly">Bi-weekly</MenuItem>
                                                    <MenuItem value="monthly">Monthly</MenuItem>
                                                    <MenuItem value="quarterly">Quarterly</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                These settings control how content is generated and optimized for your property.
                                                Changes will take effect on the next content cycle.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* WordPress Configuration Tab */}
                            {activeSettingsTab === 'wordpress' && (
                                <Box sx={{ space: 3 }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="WordPress Site URL"
                                                type="url"
                                                value={propertySettingsForm.site_url || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, site_url: e.target.value })}
                                                placeholder="https://example.com"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Username"
                                                value={propertySettingsForm.username || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, username: e.target.value })}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Password"
                                                type="password"
                                                value={propertySettingsForm.password || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, password: e.target.value })}
                                                placeholder={wordpressConfig ? '••••••••' : 'Enter password'}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Default Post Status</InputLabel>
                                                <Select
                                                    value={propertySettingsForm.default_status || 'publish'}
                                                    onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, default_status: e.target.value })}
                                                    label="Default Post Status"
                                                >
                                                    <MenuItem value="publish">Publish</MenuItem>
                                                    <MenuItem value="draft">Draft</MenuItem>
                                                    <MenuItem value="private">Private</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Default Category"
                                                value={propertySettingsForm.default_category || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, default_category: e.target.value })}
                                                placeholder="e.g., General"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Default Tags (comma-separated)"
                                                value={Array.isArray(propertySettingsForm.default_tags) ? propertySettingsForm.default_tags.join(', ') : ''}
                                                onChange={(e) => setPropertySettingsForm({
                                                    ...propertySettingsForm,
                                                    default_tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                                                })}
                                                placeholder="tag1, tag2, tag3"
                                            />
                                        </Grid>
                                    </Grid>

                                    <Box sx={{ mt: 4 }}>
                                        <Typography variant="h6" gutterBottom>Publishing Options</Typography>
                                        <Stack spacing={2}>
                                            <FormControl>
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox
                                                        checked={propertySettingsForm.is_enabled || false}
                                                        onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, is_enabled: e.target.checked })}
                                                    />
                                                    <Typography>Enable WordPress Publishing</Typography>
                                                </Box>
                                            </FormControl>
                                            <FormControl>
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox
                                                        checked={propertySettingsForm.auto_publish || false}
                                                        onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, auto_publish: e.target.checked })}
                                                    />
                                                    <Typography>Auto-publish content without review</Typography>
                                                </Box>
                                            </FormControl>
                                            <FormControl>
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox
                                                        checked={propertySettingsForm.featured_image_enabled !== false}
                                                        onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, featured_image_enabled: e.target.checked })}
                                                    />
                                                    <Typography>Enable featured image support</Typography>
                                                </Box>
                                            </FormControl>
                                            <FormControl>
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox
                                                        checked={propertySettingsForm.seo_meta_enabled !== false}
                                                        onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, seo_meta_enabled: e.target.checked })}
                                                    />
                                                    <Typography>Enable SEO meta fields</Typography>
                                                </Box>
                                            </FormControl>
                                            <FormControl>
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox
                                                        checked={propertySettingsForm.auto_slug_generation !== false}
                                                        onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, auto_slug_generation: e.target.checked })}
                                                    />
                                                    <Typography>Auto-generate slugs from titles</Typography>
                                                </Box>
                                            </FormControl>
                                        </Stack>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {activeSettingsTab === 'property' && (
                        <Button color="error" onClick={handleOpenDeleteProperty} disabled={isDeleting}>
                            {isDeleting ? 'Deleting...' : 'Delete Property'}
                        </Button>
                    )}
                    <Button onClick={handleSettingsModalClose}>Cancel</Button>
                    {activeSettingsTab === 'property' && (
                        <Button
                            variant="contained"
                            onClick={handlePropertySettingsSave}
                            disabled={isSettingsSaving}
                        >
                            {isSettingsSaving ? 'Saving...' : 'Save Property Settings'}
                        </Button>
                    )}
                    {activeSettingsTab === 'wordpress' && (
                        <Button
                            variant="contained"
                            onClick={handleWordPressConfigSave}
                            disabled={isSettingsSaving}
                        >
                            {isSettingsSaving ? 'Saving...' : 'Save WordPress Config'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Delete Property Confirmation */}
            <Dialog open={deleteDialogOpen} onClose={handleCancelDeleteProperty} maxWidth="sm" fullWidth>
                <DialogTitle>Delete Property</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <AlertTitle>Warning</AlertTitle>
                        Deleting this property is irreversible and will remove all associated configurations.
                    </Alert>
                    <Typography sx={{ mb: 2 }}>
                        To confirm, type the property name:
                        <strong> {managedProperties.find(p => p.id == selectedPropertyForManagement)?.friendly_name || ''}</strong>
                    </Typography>
                    <TextField
                        fullWidth
                        label="Property name"
                        value={deleteConfirmationText}
                        onChange={(e) => setDeleteConfirmationText(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDeleteProperty}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleConfirmDeleteProperty} disabled={isDeleting}>
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Property Details Modal */}
            <Dialog open={propertyDetailsModalOpen} onClose={() => setPropertyDetailsModalOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Property Details - {managedProperties.find(p => p.id === selectedPropertyForManagement)?.friendly_name}</DialogTitle>
                <DialogContent>
                    {selectedPropertyForManagement && managedProperties.find(p => p.id === selectedPropertyForManagement) ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>Basic Information</Typography>
                                <Typography variant="body2"><strong>ID:</strong> {managedProperties.find(p => p.id === selectedPropertyForManagement).id}</Typography>
                                <Typography variant="body2"><strong>Friendly Name:</strong> {managedProperties.find(p => p.id === selectedPropertyForManagement).friendly_name}</Typography>
                                <Typography variant="body2"><strong>GA4 Property ID:</strong> {managedProperties.find(p => p.id === selectedPropertyForManagement).ga4_property_id || 'N/A'}</Typography>
                                <Typography variant="body2"><strong>GSC Property URL:</strong> {managedProperties.find(p => p.id === selectedPropertyForManagement).gsc_property_url || 'N/A'}</Typography>
                                <Typography variant="body2"><strong>Status:</strong> {managedProperties.find(p => p.id === selectedPropertyForManagement).is_active_for_cycles ? 'Active' : 'Inactive'}</Typography>
                            </Grid>
                        </Grid>
                    ) : (
                        <Typography variant="body1" color="textSecondary">
                            No property selected or property not found.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPropertyDetailsModalOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
};

export default CASManagement; 